import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  Linking,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
  PixelRatio,
} from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { LinearGradient } from 'expo-linear-gradient';
import { Asset } from 'expo-asset';
import { useIsFocused, useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Header, Footer } from '../../components';
import { homeBlock, isHomeBlockEnabled, isHomeFeatureEnabled } from './homeContent';
import { DiscoveryHeroFrame } from '../../components/discovery/DiscoveryHeroFrame';
import { OfflineEventDetailsModal } from '../../components/livesCalendar';
import { Button } from '../../components/ui';
import { useAuth } from '../../context/AuthContext';
import { useAuthCTA } from '../../context/AuthCTAContext';
import { useCalendar } from '../../context/CalendarContext';
import { useTheme } from '../../context/ThemeContext';
import { useIsGamificationParticipant } from '../../hooks/useIsGamificationParticipant';
import { useCurrentLanguage } from '../../i18n';
import { RootStackParamList } from '../../navigation/types';
import { api, getFileUrl, getShareOrigin } from '../../services/api';
import { pickImageVariant } from '../../utils/imageVariant';
import { publishMasterSubscriptionChanged } from '../../services/masterSubscriptionEvents';
import {
  AuthState,
  HomeSection,
  trackActionResult,
  trackCardClicked,
  trackCtaClicked,
  trackDataLoadFailed,
  trackEventRsvpClicked,
  trackHomeViewed,
  trackLiveJoinClicked,
  trackSectionViewed,
} from '../../services/homeAnalytics';
import { MarketingActions, SignupSources } from '../../services/marketingTags';
import {
  HomeCommunityItem,
  HomeContentBlock,
  HomeContentCard,
  HomeEventItem,
  HomeFeaturedMasterItem,
  HomeFeedResponse,
  GamificationProfile,
  HomeLiveNowItem,
  HomeNewMasterItem,
  PublicMasterProfile,
  UnifiedEvent,
} from '../../types/auth';
import { getCachedTimezone } from '../../services/timezone';
import { retryAsync } from '../../utils/retry';
import { useHomeMasterProfiles } from './useHomeMasterProfiles';
import { desktopHomeArtwork } from './homeHeroLayout';
import { uiFont } from '../../styles/agapellaFonts';
import { typographyTokens } from '../../styles/typographyTokens';
import {
  practiceDirectionsUrl,
  type PracticeDirectionSlug,
} from '../practices/practiceDirectionFilters';
import {
  DISCOVERY_GRID_HALF_GAP,
  DISCOVERY_SHELL_MAX_WIDTH,
  discoveryPageGutter,
} from '../discovery/discoveryGridLayout';
import { buildNewsArticles, type NewsArticle } from '../news/newsModel';
import { resolveUpcomingCardAction } from './upcomingCardAction';
import {
  resolveHomeLightPathState,
  type HomeLightPathRenderState,
} from './lightPathPersonalization';
import {
  enrichHomeMastersWithProfiles,
  formatHomeMasterCardMeta,
  resolveFeaturedHomeMasters,
  resolveHomeMasterCardMeta,
} from './masterCardMeta';
import { HomeCardCarousel } from './HomeCardCarousel';
import {
  homeEventRegistrationKey,
  parseHomeRoomCode,
  parseUnifiedEventRef,
  resolveHomeEventRegistrationRef,
  resolveHomeLiveEventId,
} from './homeEventRegistration';
import { GuestAgapellaSection } from './GuestAgapellaSection';
import { HomeDirections } from './HomeDirections';
import { useHomeRoulette } from './useHomeRoulette';
import { resolveHomeHeroCopy } from './homeHeroCopy';
import { DesktopLotusSkeleton } from './HomeDesktopLotusSkeleton';
import { DEFAULT_ASSISTANT_ID } from '../../constants/chat';
import { requestOpenAgapellaWidget } from '../../widget/widgetOpenRequest';
import { HomeInfoCard } from './HomeInfoCard';
import { ShareModal } from '../../components/ShareModal';
import { shareOrOpenModal } from '../../utils/roomShare';
import { AppDownloadSection } from './AppDownloadSection';

import { ACCENT_TEXT, ON_ACCENT_TEXT } from '../../styles/accentText';
type NavigationProp = NativeStackNavigationProp<RootStackParamList>;
type HomeMasterItem = HomeFeaturedMasterItem | HomeNewMasterItem;

const environmentImage = require('../../../assets/images/circle-community.png');
const heroLandscapeLightImage = require('../../../assets/home/approved-hero-light.webp');
const heroLandscapeDarkImage = require('../../../assets/home/approved-hero-dark.webp');
const pathLandscapeImage = require('../../../assets/home/approved-path-landscape.webp');

const HOME_BLOCK_RADIUS = 24;
/**
 * CORE-1384 — ширины, которыми главная показывает картинки, в точках.
 *
 * Не «примерно», а замеренные: обложка события/эфира стоит в контейнере
 * шириной 132 (`compactImageWrap`), карточка мастера на десктопе — 323. Цифры
 * нужны, чтобы выбрать вариант; попадание в соседнюю ступень лесенки от
 * небольшой ошибки не меняется, но врать тут незачем.
 */
const COVER_DISPLAY_WIDTH = 132;
// CORE-1384 — подпись ведущего под карточкой. Замер QA 28.09: 22×22 на
// экране, а качался оригинал 512×512. Число здесь, а не по месту, чтобы
// следующая карточка с той же подписью не завела своё.
const HOST_AVATAR_DISPLAY_WIDTH = 22;
const MASTER_CARD_DISPLAY_WIDTH = 323;
/** Inner padding of a master strip; the mobile carousel bleeds over it. */
const MASTER_STRIP_PADDING = 18;

const INITIALS_PALETTE = ['#E98245', '#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', '#EC4899', '#06B6D4', '#84CC16'];
const CONTENT_CARD_FALLBACK_ICONS = ['search-outline', 'person-outline', 'leaf-outline', 'calendar-outline'];
const CAPABILITY_ICONS = ['desktop-outline', 'sparkles-outline', 'people-outline', 'chatbubbles-outline', 'calendar-outline', 'library-outline'];

const SECTION_ERROR_KEYS: Record<HomeSection, string[]> = {
  header: ['header'],
  hero: ['hero'],
  // Блок статичен, с сервера не грузится — ошибок у него не бывает.
  app_download: [],
  how_it_works: ['howItWorks', 'how_it_works'],
  live_now: ['liveNow', 'live_now'],
  upcoming_events: ['upcomingEvents', 'upcoming_events'],
  featured_masters: ['featuredMasters', 'featured_masters'],
  new_masters: ['newMasters', 'new_masters'],
  communities: ['communities'],
  news: ['news'],
  environment: ['environment'],
  capabilities: ['capabilities'],
  light_path: ['pathOfLight', 'lightPath', 'light_path'],
  final_cta: ['finalCta', 'final_cta'],
};

const SECTION_FROM_ERROR_KEY: Record<string, HomeSection> = Object.entries(SECTION_ERROR_KEYS)
  .reduce((acc, [section, keys]) => {
    keys.forEach((key) => { acc[key] = section as HomeSection; });
    return acc;
  }, {} as Record<string, HomeSection>);

const copy = {
  ru: {
    intentionsTitle: 'О ком и о чём мне хочется позаботиться сегодня?',
    intentionsSubtitle: 'Выберите то, с чего хотите начать —\nAgapella поможет найти практики, людей и подходящий путь.',
    intentionsUnknown: 'Не знаю, с чего начать.',
    intentionsAsk: 'Спросить Agapella',
    heroFallbackTitle: 'Платформа знаний и практик в поддержку человека',
    heroFallbackSubtitle: 'Живое общение с мастерами разных школ и традиций\nLive-сессии, библиотека видео и аудио практик на каждый день\nОнлайн и офлайн события, личный календарь\nAI-помощник для настройки вашего персонального опыта\nБережное окружение и сообщества, объединяющие людей на пути развития\n24/7 в любой точке мира',
    liveNow: 'Сейчас идут Live-сессии',
    liveNowSubtitle: 'Подключайтесь к live-сессиям, которые идут прямо сейчас.',
    upcoming: 'Ближайшие события',
    upcomingSubtitle: 'Live, онлайн- и офлайн-встречи в ближайшем расписании.',
    featuredMasters: 'Популярные мастера',
    newMasters: 'Новые мастера',
    communities: 'Сообщества',
    news: 'Новости Agapella',
    capabilities: 'Возможности Agapella',
    calmPathTitle: 'Следуй своему пути вместе с Agapella',
    calmPathBody: 'Практики и поддержка — в удобном для тебя ритме.',
    lightPath: 'Следуй своему пути вместе с Agapella',
    lightPathSubtitle: 'Знания. Практики. Поддержка. Для жизни в гармонии с собой и миром.',
    finalCta: 'Начать путь',
    seeAll: 'Смотреть все',
    details: 'Подробнее',
    follow: 'Подписаться',
    following: 'Вы подписаны',
    followAuth: 'Войдите, чтобы подписаться на мастера.',
    followError: 'Не удалось изменить подписку',
    open: 'Открыть',
    join: 'Присоединиться',
    joinLive: 'Войти в Live-сессию',
    rsvp: 'Записаться',
    going: 'Вы идёте',
    rsvpUnavailable: 'Недоступно',
    rsvpError: 'Не удалось записаться. Попробуйте ещё раз.',
    participants: 'участников',
    attendees: 'участников',
    master: 'Мастер',
    emptyLive: 'Сейчас нет активных Live-сессий.',
    emptyUpcoming: 'Ближайшие события пока не опубликованы.',
    emptyMasters: 'Подборка мастеров пока не опубликована.',
    emptyNews: 'Новости пока не опубликованы.',
    loading: 'Загружаем главную...',
    retry: 'Повторить',
    loadError: 'Не удалось загрузить главную.',
    sectionError: 'Секция временно недоступна.',
    authLive: 'Войдите, чтобы присоединиться к Live-сессии.',
    authEvent: 'Войдите, чтобы записаться на событие.',
    authAgapella: 'Войдите, чтобы спросить Agapella.',
    authLightPathHeading: 'Сохраните свой ритм',
    authLightPath: 'Войдите или создайте аккаунт, чтобы возвращаться к практикам и замечать свой прогресс.',
    authGuestChat: 'Заведите аккаунт — и мы продолжим с этого же места.',
    lightPathGuestTitle: 'Начни свой Путь Света вместе с Agapella',
    lightPathGuestSubtitle: 'Мягкий личный маршрут поможет возвращаться к практикам, отмечать живой ритм и видеть, что поддерживает тебя каждый день.',
    lightPathGuestCta: 'Начать путь',
    lightPathLearn: 'Узнать, как это работает',
    lightPathPersonalTitle: 'Продолжай свой Путь Света',
    lightPathErrorTitle: 'Твой Путь Света',
    lightPathErrorSubtitle: 'Личные метрики сейчас не загрузились. Раздел можно открыть без них и вернуться к данным позже.',
    lightPathOpen: 'Открыть раздел',
    lightPathContinue: 'Продолжить путь',
    lightPathFlame: 'Пламя Любви',
    lightPathSoul: 'Свет Души',
    lightPathLevel: 'Уровень',
    lightPathProgress: 'до следующего уровня',
    howTitle: 'Как работает наша платформа',
    howSubtitle: 'Agapella — это поддерживающее пространство для практик, осознанных встреч и коллабораций, живого взаимодействия мастеров и участников.',
    howCta: 'Узнать больше',
    environmentFallbackTitle: 'Найди своё окружение',
    environmentFallbackSubtitle: 'Находите единомышленников рядом с вами или онлайн. Участвуйте во встречах, обсуждениях и совместных практиках.',
  },
  en: {
    intentionsTitle: 'Who or what would I like to care for today?',
    intentionsSubtitle: 'Choose where you would like to begin — Agapella will help you find practices, people and a path that fits.',
    intentionsUnknown: 'Not sure where to begin.',
    intentionsAsk: 'Ask Agapella',
    heroFallbackTitle: 'A human-centered platform for knowledge and practice',
    heroFallbackSubtitle: 'Agapella brings together trusted masters, live practices and supportive community so people can grow with steadiness and care.',
    liveNow: 'Live now',
    liveNowSubtitle: 'Join public live sessions that are happening now.',
    upcoming: 'Upcoming events',
    upcomingSubtitle: 'Live, online and offline meetings coming soon.',
    featuredMasters: 'Featured masters',
    newMasters: 'New masters',
    communities: 'Communities',
    news: 'Agapella news',
    capabilities: 'Agapella capabilities',
    calmPathTitle: 'Follow your path with Agapella',
    calmPathBody: 'Practices and support at your own pace.',
    lightPath: 'Follow your path with Agapella',
    lightPathSubtitle: 'Knowledge. Practice. Support. For a life in harmony with yourself and the world.',
    finalCta: 'Start the path',
    seeAll: 'See all',
    details: 'Details',
    follow: 'Follow',
    following: 'Following',
    followAuth: 'Sign in to follow this master.',
    followError: 'Could not update follow',
    open: 'Open',
    join: 'Join',
    joinLive: 'Join live',
    rsvp: 'RSVP',
    going: 'Going',
    rsvpUnavailable: 'Unavailable',
    rsvpError: 'Could not register. Please try again.',
    participants: 'participants',
    attendees: 'attendees',
    master: 'Master',
    emptyLive: 'No live sessions are running right now.',
    emptyUpcoming: 'No upcoming events have been published yet.',
    emptyMasters: 'This masters selection has not been published yet.',
    emptyNews: 'No news has been published yet.',
    loading: 'Loading home...',
    retry: 'Retry',
    loadError: 'Could not load the home page.',
    sectionError: 'This section is temporarily unavailable.',
    authLive: 'Sign in to join the live session.',
    authEvent: 'Sign in to RSVP.',
    authAgapella: 'Sign in to ask Agapella.',
    authLightPathHeading: 'Keep your rhythm',
    authLightPath: 'Sign in or create an account to return to your practices and notice your progress.',
    authGuestChat: 'Create an account and we pick up right where we left off.',
    lightPathGuestTitle: 'Start your Path of Light with Agapella',
    lightPathGuestSubtitle: 'A gentle personal route helps you return to practice, keep your rhythm alive and see what supports you each day.',
    lightPathGuestCta: 'Start the path',
    lightPathLearn: 'See how it works',
    lightPathPersonalTitle: 'Continue your Path of Light',
    lightPathErrorTitle: 'Your Path of Light',
    lightPathErrorSubtitle: 'Your personal metrics did not load right now. You can still open the section and return to the data later.',
    lightPathOpen: 'Open Path of Light',
    lightPathContinue: 'Continue',
    lightPathFlame: 'Flame of Love',
    lightPathSoul: 'Soul Light',
    lightPathLevel: 'Level',
    lightPathProgress: 'to next level',
    howTitle: 'How our platform works',
    howSubtitle: 'Agapella is a supportive space for practices, conscious meetings and collaborations, for live interaction between masters and participants.',
    howCta: 'Learn more',
    environmentFallbackTitle: 'Find your environment',
    environmentFallbackSubtitle: 'Meet like-minded people nearby or online through conversations, events and shared practices.',
  },
  es: {
    intentionsTitle: '¿De quién o de qué quiero cuidar hoy?',
    intentionsSubtitle: 'Elige por dónde empezar: Agapella te ayudará a encontrar prácticas, personas y un camino adecuado.',
    intentionsUnknown: 'No sé por dónde empezar.',
    intentionsAsk: 'Preguntar a Agapella',
    heroFallbackTitle: 'Una plataforma humanista de conocimiento y práctica',
    heroFallbackSubtitle: 'Agapella reúne maestros, prácticas en vivo y una comunidad de apoyo para crecer con calma y cuidado.',
    liveNow: 'En vivo ahora',
    liveNowSubtitle: 'Únete a sesiones públicas que están ocurriendo ahora.',
    upcoming: 'Próximos eventos',
    upcomingSubtitle: 'Encuentros live, online y offline próximos.',
    featuredMasters: 'Maestros populares',
    newMasters: 'Nuevos maestros',
    communities: 'Comunidades',
    news: 'Noticias de Agapella',
    capabilities: 'Capacidades de Agapella',
    calmPathTitle: 'Sigue tu camino con Agapella',
    calmPathBody: 'Prácticas y apoyo a tu ritmo.',
    lightPath: 'Sigue tu camino con Agapella',
    lightPathSubtitle: 'Conocimiento. Práctica. Apoyo. Para vivir en armonía contigo y con el mundo.',
    finalCta: 'Empezar el camino',
    seeAll: 'Ver todo',
    details: 'Más detalles',
    follow: 'Seguir',
    following: 'Siguiendo',
    followAuth: 'Inicia sesión para seguir a este maestro.',
    followError: 'No se pudo actualizar el seguimiento',
    open: 'Abrir',
    join: 'Unirse',
    joinLive: 'Entrar en vivo',
    rsvp: 'Reservar',
    going: 'Asistirás',
    rsvpUnavailable: 'No disponible',
    rsvpError: 'No se pudo completar la reserva. Inténtalo de nuevo.',
    participants: 'participantes',
    attendees: 'asistentes',
    master: 'Maestro',
    emptyLive: 'No hay sesiones en vivo ahora.',
    emptyUpcoming: 'Aún no hay eventos próximos publicados.',
    emptyMasters: 'La selección de maestros aún no está publicada.',
    emptyNews: 'Aún no hay noticias publicadas.',
    loading: 'Cargando inicio...',
    retry: 'Reintentar',
    loadError: 'No se pudo cargar la página inicial.',
    sectionError: 'Esta sección no está disponible temporalmente.',
    authLive: 'Inicia sesión para unirte.',
    authEvent: 'Inicia sesión para reservar.',
    authAgapella: 'Inicia sesión para preguntar a Agapella.',
    authLightPathHeading: 'Mantén tu ritmo',
    authLightPath: 'Inicia sesión o crea una cuenta para volver a tus prácticas y observar tu progreso.',
    authGuestChat: 'Crea una cuenta y seguimos justo desde aquí.',
    lightPathGuestTitle: 'Empieza tu Camino de la Luz con Agapella',
    lightPathGuestSubtitle: 'Una ruta personal suave te ayuda a volver a la práctica, cuidar tu ritmo y ver qué te sostiene cada día.',
    lightPathGuestCta: 'Empezar camino',
    lightPathLearn: 'Ver cómo funciona',
    lightPathPersonalTitle: 'Continúa tu Camino de la Luz',
    lightPathErrorTitle: 'Tu Camino de la Luz',
    lightPathErrorSubtitle: 'Tus métricas personales no se cargaron ahora. Puedes abrir la sección y volver a los datos más tarde.',
    lightPathOpen: 'Abrir Camino de la Luz',
    lightPathContinue: 'Continuar',
    lightPathFlame: 'Llama del Amor',
    lightPathSoul: 'Luz del Alma',
    lightPathLevel: 'Nivel',
    lightPathProgress: 'para el siguiente nivel',
    howTitle: 'Cómo funciona nuestra plataforma',
    howSubtitle: 'Agapella es un espacio de apoyo para prácticas, encuentros conscientes y colaboraciones, para la interacción viva entre maestros y participantes.',
    howCta: 'Saber más',
    environmentFallbackTitle: 'Encuentra tu entorno',
    environmentFallbackSubtitle: 'Conoce personas afines cerca de ti u online a través de encuentros, conversaciones y prácticas.',
  },
};

/**
 * CORE-1091 — the language here has to be REACTIVE, and used to not be.
 *
 * This screen shows two layers of copy: `labels` from the table above, and the
 * editorial text the backend returns from `GET /api/home?locale=…` on top of
 * it. Both key off this value, and the fetch re-runs because `langKey` is in
 * `load`'s dependency list.
 *
 * Reading `getCurrentLanguage()` (a bare `i18n.language`) made that dependency
 * a lie: nothing re-rendered this screen when the language changed, so `load`
 * kept its identity and the feed was never refetched. The header switched —
 * it consumes react-i18next — and the editorial blocks stayed Russian until a
 * manual reload. QA caught exactly that on 27.08.2026, after the backend half
 * of the ticket had already shipped.
 *
 * `useCurrentLanguage()` subscribes; do not replace it with the plain getter.
 */
function useLanguageKey(): keyof typeof copy {
  const lang = useCurrentLanguage();
  if (lang.startsWith('en')) return 'en';
  if (lang.startsWith('es')) return 'es';
  return 'ru';
}

function initials(name: string | null | undefined): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (parts[0]?.slice(0, 2) || 'A').toUpperCase();
}

// CORE-1123 — правила чтения опубликованных блоков переехали в `homeContent`:
// теперь их читает и приложение, а две копии означали бы, что «выключено» на
// вебе и «выключено» в приложении со временем разойдутся. Поведение прежнее,
// функции те же — здесь остались только локальные имена.
const block = (feed: HomeFeedResponse | null, key: string): HomeContentBlock | null =>
  homeBlock(feed, key);

const isBlockEnabled = (feed: HomeFeedResponse | null, key: string): boolean =>
  isHomeBlockEnabled(feed, key);

const isFeatureEnabled = (feed: HomeFeedResponse | null, featureKey: string): boolean =>
  isHomeFeatureEnabled(feed, featureKey);

function sectionError(feed: HomeFeedResponse | null, section: HomeSection): string | null {
  const errors = feed?.sectionErrors || {};
  for (const key of SECTION_ERROR_KEYS[section]) {
    const value = errors[key];
    if (value) return String(value);
  }
  return null;
}

function formatCount(value: number | null | undefined): string {
  const count = Number(value || 0);
  if (count >= 1000) {
    const fixed = (count / 1000).toFixed(count >= 10000 ? 0 : 1).replace('.', ',');
    return `${fixed} тыс.`;
  }
  return String(count);
}

function formatDateTime(iso: string | null | undefined, locale: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function mergeHomeMasters(...groups: HomeMasterItem[][]): HomeMasterItem[] {
  const seen = new Set<number>();
  const merged: HomeMasterItem[] = [];
  groups.flat().forEach((master) => {
    if (seen.has(master.id)) return;
    seen.add(master.id);
    merged.push(master);
  });
  return merged;
}

function contentCards(blockData: HomeContentBlock | null | undefined): HomeContentCard[] {
  return blockData?.cards?.filter((card) => card.title || card.text) || [];
}

function contentCtasAsCards(blockData: HomeContentBlock | null | undefined): HomeContentCard[] {
  return (blockData?.cta || [])
    .filter((cta) => cta.label || cta.url)
    .map((cta) => ({
      title: cta.label,
      text: null,
      url: cta.url,
      ctaLabel: null,
      iconUrl: null,
      imageUrl: null,
      coverUrl: null,
    }));
}

function isOfflineHomeEvent(item: HomeEventItem): boolean {
  return (item.type || '').toLowerCase() === 'offline';
}

function normalizeInternalUrl(raw: string | null | undefined): string | null {
  if (!raw) return null;
  if (raw.startsWith('/')) return raw;
  try {
    const u = new URL(raw);
    if (u.hostname.endsWith('agapella.io') || u.hostname.endsWith('vercel.app') || u.hostname.endsWith('tmless.org')) {
      return `${u.pathname}${u.search}${u.hash}`;
    }
  } catch {
    return null;
  }
  return null;
}

function readNumericSegment(path: string, patterns: RegExp[]): number | null {
  for (const pattern of patterns) {
    const match = path.match(pattern);
    if (match?.[1]) {
      const value = Number(match[1]);
      if (Number.isFinite(value)) return value;
    }
  }
  return null;
}

const sectionShell = (isDark: boolean) => ({
  backgroundColor: isDark ? 'transparent' : 'rgba(255, 250, 244, 0.58)',
  borderColor: isDark ? 'transparent' : 'rgba(171, 105, 54, 0.24)',
  ...(Platform.OS === 'web' ? {
    boxShadow: isDark ? 'none' : '0 18px 54px rgba(158, 111, 70, 0.08)',
  } as any : null),
});

const cardShell = (isDark: boolean) => ({
  backgroundColor: isDark ? '#292D30' : '#FFFCF2',
  ...(Platform.OS === 'web' ? { backgroundImage: isDark ? 'none' : 'linear-gradient(135deg, #FFFCF2, #EDE5D8)' } as any : null),
  borderColor: isDark ? '#545658' : 'rgba(171, 105, 54, 0.22)',
});

const TrackableSection: React.FC<{
  section: HomeSection;
  style?: any;
  children: React.ReactNode;
}> = ({ section, style, children }) => {
  const ref = useRef<any>(null);
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    if (Platform.OS !== 'web' || typeof IntersectionObserver === 'undefined') {
      firedRef.current = true;
      trackSectionViewed(section);
      return;
    }
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver((entries) => {
      if (firedRef.current) return;
      if (entries.some((entry) => entry.isIntersecting)) {
        firedRef.current = true;
        trackSectionViewed(section);
        observer.disconnect();
      }
    }, { threshold: 0.28 });
    observer.observe(node);
    return () => observer.disconnect();
  }, [section]);

  return (
    <View ref={ref} style={style} collapsable={false}>
      {children}
    </View>
  );
};

const SectionHeader: React.FC<{
  title: string;
  subtitle?: string | null;
  actionLabel?: string;
  onAction?: () => void;
  compact?: boolean;
  isDark: boolean;
}> = ({ title, subtitle, actionLabel, onAction, compact = false, isDark }) => (
  <View style={[styles.sectionHeader, compact && styles.sectionHeaderCompact]}>
    <View style={styles.sectionTitleBlock}>
      <Text style={[styles.sectionTitle, { color: isDark ? '#F3ECE3' : '#332720' }]}>{title}</Text>
      {subtitle ? <Text style={[styles.sectionSubtitle, { color: isDark ? '#B9B0B6' : '#625B67' }]}>{subtitle}</Text> : null}
    </View>
    {actionLabel && onAction ? (
      <TouchableOpacity onPress={onAction} activeOpacity={0.75} style={styles.sectionAction} accessibilityRole="button">
        <Text style={styles.sectionActionText}>{actionLabel}</Text>
        <Ionicons name="arrow-forward" size={14} color={ACCENT_TEXT} />
      </TouchableOpacity>
    ) : null}
  </View>
);

const SectionState: React.FC<{
  loading?: boolean;
  error?: string | null;
  empty?: string | null;
  isDark: boolean;
}> = ({ loading, error, empty, isDark }) => {
  if (loading) {
    return (
      <View style={styles.stateBox}>
        <ActivityIndicator color={ACCENT_TEXT} />
      </View>
    );
  }
  if (error) {
    return (
      <View style={[styles.stateBox, cardShell(isDark)]}>
        <Ionicons name="alert-circle-outline" size={20} color={ACCENT_TEXT} />
        <Text style={[styles.stateText, { color: isDark ? '#C7BEC5' : '#625B67' }]}>{error}</Text>
      </View>
    );
  }
  if (empty) {
    return (
      <View style={[styles.stateBox, cardShell(isDark)]}>
        <Ionicons name="calendar-clear-outline" size={20} color={ACCENT_TEXT} />
        <Text style={[styles.stateText, { color: isDark ? '#C7BEC5' : '#625B67' }]}>{empty}</Text>
      </View>
    );
  }
  return null;
};

const HowItWorksCtaSection: React.FC<{
  isDark: boolean;
  onOpen: (url: string | null | undefined) => void;
}> = ({ isDark, onOpen }) => {
  const labels = copy[useLanguageKey()];
  return <TrackableSection section="how_it_works"><HomeInfoCard
    testID="home-how-it-works-panel" ctaTestID="home-how-it-works-cta"
    kind="platform" title={labels.howTitle} body={labels.howSubtitle}
    cta={labels.howCta} onPress={() => onOpen('/about')} isDark={isDark}
  /></TrackableSection>;
};

type HomeGamificationState = {
  ownerKey: string | null;
  loading: boolean;
  profile: GamificationProfile | null;
  error: string | null;
};

type IntentionItem = {
  title: string;
  directions: string;
  slugs: readonly PracticeDirectionSlug[];
  icon: React.ComponentProps<typeof Ionicons>['name'];
};

const practiceDirectionAnalyticsSlug = (slug: PracticeDirectionSlug): string => {
  if (slug === 'before-sleep') return 'sleep';
  if (slug === 'body-practices') return 'body';
  if (slug === 'emotional-intelligence') return 'eq';
  if (slug === 'morning-practice') return 'morning';
  if (slug === 'short-practice') return 'short';
  return slug;
};

const intentionsByLanguage: Record<'ru' | 'en' | 'es', IntentionItem[]> = {
  ru: [
    { title: 'Исследую, кто я', directions: 'Медитации · Традиции', slugs: ['meditation', 'traditions'], icon: 'leaf-outline' },
    { title: 'Побеждаю тревогу и бессонницу', directions: 'Дыхательная гимнастика · Практики перед сном', slugs: ['breathwork', 'before-sleep'], icon: 'moon-outline' },
    { title: 'Начинаю день в ресурсе', directions: 'Утренние · Короткие практики', slugs: ['morning-practice', 'short-practice'], icon: 'sunny-outline' },
    { title: 'Выбираю заботу о здоровье', directions: 'Здоровое тело · Долголетие', slugs: ['healthy-body', 'longevity'], icon: 'body-outline' },
    { title: 'Хочу лучше понимать себя и близких', directions: 'Философия · Эмоциональный интеллект', slugs: ['philosophy', 'emotional-intelligence'], icon: 'heart-outline' },
    { title: 'Ищу опору', directions: 'Телесные практики · Психология', slugs: ['body-practices', 'psychology'], icon: 'library-outline' },
  ],
  en: [
    { title: 'Discover who I am', directions: 'Meditation · Traditions', slugs: ['meditation', 'traditions'], icon: 'leaf-outline' },
    { title: 'Overcome anxiety and insomnia', directions: 'Breathwork · Before sleep', slugs: ['breathwork', 'before-sleep'], icon: 'moon-outline' },
    { title: 'Begin my day resourced', directions: 'Morning · Short practices', slugs: ['morning-practice', 'short-practice'], icon: 'sunny-outline' },
    { title: 'Choose to care for my health', directions: 'Healthy body · Longevity', slugs: ['healthy-body', 'longevity'], icon: 'body-outline' },
    { title: 'Understand myself and those close to me better', directions: 'Philosophy · Emotional intelligence', slugs: ['philosophy', 'emotional-intelligence'], icon: 'heart-outline' },
    { title: 'Find support', directions: 'Body practices · Psychology', slugs: ['body-practices', 'psychology'], icon: 'library-outline' },
  ],
  es: [
    { title: 'Exploro quién soy', directions: 'Meditación · Tradiciones', slugs: ['meditation', 'traditions'], icon: 'leaf-outline' },
    { title: 'Supero la ansiedad y el insomnio', directions: 'Respiración · Antes de dormir', slugs: ['breathwork', 'before-sleep'], icon: 'moon-outline' },
    { title: 'Empiezo el día con energía', directions: 'Prácticas matutinas · Cortas', slugs: ['morning-practice', 'short-practice'], icon: 'sunny-outline' },
    { title: 'Elijo cuidar mi salud', directions: 'Cuerpo sano · Longevidad', slugs: ['healthy-body', 'longevity'], icon: 'body-outline' },
    { title: 'Comprenderme mejor a mí y a mis seres queridos', directions: 'Filosofía · Inteligencia emocional', slugs: ['philosophy', 'emotional-intelligence'], icon: 'heart-outline' },
    { title: 'Busco apoyo', directions: 'Prácticas corporales · Psicología', slugs: ['body-practices', 'psychology'], icon: 'library-outline' },
  ],
};

const DESKTOP_PETAL_PLACEMENT = [
  { contentLeft: 117, contentTop: 28, contentWidth: 130, contentHeight: 76 },
  { contentLeft: 282, contentTop: 14, contentWidth: 144, contentHeight: 112 },
  { contentLeft: 367, contentTop: 151, contentWidth: 130, contentHeight: 110 },
  { contentLeft: 294, contentTop: 277, contentWidth: 130, contentHeight: 112 },
  { contentLeft: 116, contentTop: 279, contentWidth: 130, contentHeight: 126 },
  { contentLeft: 37, contentTop: 158, contentWidth: 130, contentHeight: 92 },
] as const;


const HeroBenefits: React.FC<{
  subtitle: string;
  isDark: boolean;
}> = ({ subtitle, isDark }) => {
  const benefits = subtitle.split(/\n+|(?<=[.!?])\s+/u).filter(Boolean);

  return (
    <View testID="home-hero-benefits" style={[styles.heroBenefits, isDark ? styles.heroBenefitsDark : styles.heroBenefitsLight]}>
      <View testID="home-hero-benefits-haze" pointerEvents="none" style={[styles.heroBenefitsHaze, isDark && styles.heroBenefitsHazeDark]} />
      {benefits.map((benefit) => (
        <View key={benefit} style={styles.heroBenefitRow}>
          <View pointerEvents="none" style={styles.heroBenefitSymbol}>
            <LinearGradient
              colors={isDark
                ? ['#FFF3D5', '#FFB067', '#E75D2B']
                : ['#FFF8E8', '#FFAD5A', '#DE5527']}
              locations={[0, 0.48, 1]}
              start={{ x: 0.18, y: 0.12 }}
              end={{ x: 0.82, y: 0.88 }}
              style={styles.heroBenefitSymbolFill}
            >
              <View style={styles.heroBenefitSymbolCore} />
            </LinearGradient>
          </View>
          <Text
            style={[
              styles.heroBenefitText,
              { color: isDark ? '#E2D5CF' : '#57463E' },
            ]}
          >
            {benefit}
          </Text>
        </View>
      ))}
    </View>
  );
};

export const HomeReleaseScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const { width } = useWindowDimensions();
  const { isDark, colors: themeColors } = useTheme();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const { showAuthCTA } = useAuthCTA();
  const isGamificationParticipant = useIsGamificationParticipant();
  const {
    addToCalendar,
    reminderIds,
    offlineRegistrationIds,
    offlineRegistrationPendingIds,
    offlineRegistrationErrorIds,
    toggleOfflineEventRegistration,
    seedOfflineEventRegistration,
  } = useCalendar();
  const langKey = useLanguageKey();
  const isFocused = useIsFocused();
  // Desktop and mobile panels live in different layout branches. Keep one
  // cycle on the screen so changing width cannot discard its selected result.
  const roulette = useHomeRoulette(intentionsByLanguage[langKey].length, isFocused);
  const locale = langKey === 'ru' ? 'ru-RU' : langKey === 'es' ? 'es-ES' : 'en-US';
  const labels = copy[langKey];
  const isMobile = width < 768;
  const isDesktop = width >= 1120;
  // CORE-1022 — exactly the fixed web header's height (Header.tsx: 90 px on the
  // desktop layout, 58 px on the compact one below 1280). It used to carry an
  // extra 28/18 px of page air, which the full-bleed hero turned into a visible
  // strip of page background between the header divider and the hero artwork.
  // The air now lives inside the hero's own paddingTop, so the artwork starts
  // right under the header while the headline keeps its rhythm.
  const headerInset = Platform.OS === 'web' ? (width < 1280 ? 58 : 90) : 28;
  // Grow the artwork with the approved flower instead of clipping its bottom.
  const desktopHeroScale = 1 + Math.min(1, Math.max(0, (width - 1280) / 640)) * 0.12;
  const desktopFlowerWidth = 534 * desktopHeroScale;
  const desktopFlowerHeight = 416 * desktopHeroScale;
  const desktopHeroTop = headerInset + 48;
  const desktopArtwork = desktopHomeArtwork(width, desktopHeroScale);
  const heroArtworkHeight = isDesktop ? desktopArtwork.height : Math.max(240, width * 558 / 1672);
  const heroArtworkWidth = isDesktop ? desktopArtwork.imageWidth : Math.max(width, heroArtworkHeight * 1672 / 558);
  const heroImageHeight = isDesktop ? desktopArtwork.imageHeight : heroArtworkHeight;
  const desktopCopyWidth = Math.min(
    520 * desktopHeroScale,
    Math.min(width, DISCOVERY_SHELL_MAX_WIDTH) - 96 - desktopFlowerWidth - 72,
  );

  const [assistantOpenRequest, setAssistantOpenRequest] = useState(0);
  useEffect(() => { if (isAuthenticated) setAssistantOpenRequest(0); }, [isAuthenticated]);
  const askFlowerAssistant = useCallback(() => {
    if (!isAuthenticated) { setAssistantOpenRequest((request) => request + 1); return; }
    if (requestOpenAgapellaWidget()) return;
    navigation.navigate('Chats', { companionId: DEFAULT_ASSISTANT_ID });
  }, [isAuthenticated, navigation]);

  const [feed, setFeed] = useState<HomeFeedResponse | null>(null);
  const masterOwnerKey = authLoading ? null : isAuthenticated ? `user:${user?.id ?? 'pending'}` : 'guest';
  const masterOwnerRef = useRef(masterOwnerKey);
  masterOwnerRef.current = masterOwnerKey;
  const { masters: rankedMasters, ready: masterProfilesReady } = useHomeMasterProfiles(masterOwnerKey);
  const [masterSubscriptionOverrides, setMasterSubscriptionOverrides] = useState<Map<number, boolean>>(new Map());
  const [pendingMasterFollows, setPendingMasterFollows] = useState<Set<number>>(new Set());
  const [masterFollowErrors, setMasterFollowErrors] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState<string | null>(null);
  const [selectedOfflineEvent, setSelectedOfflineEvent] = useState<UnifiedEvent | null>(null);
  const [eventRsvpPendingKeys, setEventRsvpPendingKeys] = useState<Set<string>>(new Set());
  const [eventRsvpSuccessKeys, setEventRsvpSuccessKeys] = useState<Set<string>>(new Set());
  const [eventRsvpErrorByKey, setEventRsvpErrorByKey] = useState<Map<string, string>>(new Map());
  const eventRsvpPendingKeysRef = useRef<Set<string>>(new Set());
  const eventRsvpSuccessKeysRef = useRef<Set<string>>(new Set());
  const eventRsvpOwnerKey = isAuthenticated
    ? `user:${user?.id ?? 'pending'}`
    : 'guest';
  const eventRsvpSessionRef = useRef({ ownerKey: eventRsvpOwnerKey, generation: 0 });
  if (eventRsvpSessionRef.current.ownerKey !== eventRsvpOwnerKey) {
    eventRsvpSessionRef.current = {
      ownerKey: eventRsvpOwnerKey,
      generation: eventRsvpSessionRef.current.generation + 1,
    };
    // Reset the synchronous guards during render as well as the visible state
    // below. This closes the small render -> effect window on account changes.
    eventRsvpPendingKeysRef.current = new Set();
    eventRsvpSuccessKeysRef.current = new Set();
  }
  const [gamificationState, setGamificationState] = useState<HomeGamificationState>({
    ownerKey: null,
    loading: false,
    profile: null,
    error: null,
  });

  const authState: AuthState = isAuthenticated ? 'authenticated' : 'guest';
  const gamificationOwnerKey = isAuthenticated && isGamificationParticipant
    ? (user?.id ?? 'current-user')
    : null;

  useEffect(() => {
    eventRsvpPendingKeysRef.current = new Set();
    eventRsvpSuccessKeysRef.current = new Set();
    setEventRsvpPendingKeys(new Set());
    setEventRsvpSuccessKeys(new Set());
    setEventRsvpErrorByKey(new Map());
  }, [eventRsvpOwnerKey]);

  const navigateToUrl = useCallback((rawUrl: string | null | undefined) => {
    const internal = normalizeInternalUrl(rawUrl);
    if (!internal) {
      if (rawUrl && /^https?:\/\//i.test(rawUrl)) Linking.openURL(rawUrl).catch(() => {});
      return;
    }

    const [pathWithQuery, hash = ''] = internal.split('#');
    const [path, queryString = ''] = pathWithQuery.split('?');
    const query = new URLSearchParams(queryString);
    const liveId = readNumericSegment(path, [/^\/live\/(\d+)\/?$/, /^\/lives\/(\d+)\/?$/]);
    if (liveId != null) {
      navigation.navigate('LiveDetail', { id: liveId });
      return;
    }
    const roomMatch = path.match(/^\/room\/([^/?#]+)\/?$/);
    if (roomMatch?.[1]) {
      navigation.navigate('RoomAnnouncement', { roomId: decodeURIComponent(roomMatch[1]) });
      return;
    }
    const masterId = readNumericSegment(path, [/^\/masters\/(\d+)\/?$/]);
    if (masterId != null) {
      navigation.navigate('MasterDetail', { masterId });
      return;
    }
    const userMatch = path.match(/^\/u\/([^/?#]+)\/?$/);
    if (userMatch?.[1]) {
      navigation.navigate('PublicUserProfile', { userId: decodeURIComponent(userMatch[1]) });
      return;
    }
    if (path === '/lives' || path === '/schedule') {
      const eventId = query.get('eventId');
      navigation.navigate('Lives', { eventId: eventId ? Number(eventId) : undefined });
      return;
    }
    if (path === '/calendar' || path === '/events') {
      navigation.navigate('Lives');
      return;
    }
    if (path === '/practices') {
      navigation.navigate('Practices');
      if (Platform.OS === 'web' && hash) {
        window.setTimeout(() => {
          const nextUrl = `/practices#${hash}`;
          window.history.replaceState(window.history.state, '', nextUrl);
          window.dispatchEvent(new HashChangeEvent('hashchange'));
        }, 0);
      }
      return;
    }
    if (path === '/masters') {
      navigation.navigate('Masters', { freshAt: Date.now() });
      return;
    }
    if (path === '/chats') {
      navigation.navigate('Chats');
      return;
    }
    if (path === '/about' || path === '/about-platform') {
      navigation.navigate('AboutPlatform');
      return;
    }
    if (path === '/environment') {
      navigation.navigate('Environment' as any);
      return;
    }
    if (path === '/support') {
      navigation.navigate('Support' as any);
      return;
    }
    if (path === '/' || path === '') {
      navigation.navigate('Practices');
    }
  }, [navigation]);

  // CORE-1715 — язык может смениться, пока первый запрос ещё в полёте (веб без
  // переключателя переходит на русский после ответа `/api/features`). Ответ на
  // прежний язык, пришедший позже, не должен перекрыть ответ на текущий.
  const loadSeqRef = useRef(0);
  const load = useCallback(async () => {
    const seq = ++loadSeqRef.current;
    setLoading(true);
    setPageError(null);
    try {
      const data = await retryAsync(
        () => api.getHomeFeed({ locale: langKey, timezone: getCachedTimezone() }),
        { retries: 3, baseDelayMs: 500 },
      );
      if (seq !== loadSeqRef.current) return;
      setFeed(data);
      const errors = data.sectionErrors || {};
      Object.entries(errors).forEach(([key, value]) => {
        const section = SECTION_FROM_ERROR_KEY[key];
        if (section && value) trackDataLoadFailed({ section, errorCode: String(value) });
      });
    } catch (err: any) {
      if (seq !== loadSeqRef.current) return;
      setPageError(err?.message || labels.loadError);
      trackDataLoadFailed({ section: 'hero', errorCode: err?.code || err?.status || 'HOME_LOAD_FAILED' });
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [labels.loadError, langKey]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (authLoading) return;
    if (!gamificationOwnerKey) {
      setGamificationState({
        ownerKey: null,
        loading: false,
        profile: null,
        error: null,
      });
      return;
    }

    let alive = true;
    setGamificationState({
      ownerKey: gamificationOwnerKey,
      loading: true,
      profile: null,
      error: null,
    });

    api.getGamificationProfile()
      .then((profile) => {
        if (!alive) return;
        setGamificationState({
          ownerKey: gamificationOwnerKey,
          loading: false,
          profile,
          error: null,
        });
      })
      .catch((err: any) => {
        if (!alive) return;
        const errorCode = String(err?.code || err?.status || 'GAMIFICATION_PROFILE_FAILED');
        setGamificationState({
          ownerKey: gamificationOwnerKey,
          loading: false,
          profile: null,
          error: errorCode,
        });
        trackDataLoadFailed({ section: 'light_path', errorCode });
      });

    return () => { alive = false; };
  }, [authLoading, gamificationOwnerKey]);
  useEffect(() => { trackHomeViewed(authState); }, [authState]);

  const liveHeader = block(feed, 'liveNowHeader');
  const heroBlock = block(feed, 'hero');
  const upcomingHeader = block(feed, 'upcomingEventsHeader');
  const featuredHeader = block(feed, 'featuredMastersHeader');
  const newHeader = block(feed, 'newMastersHeader');
  const environmentBlock = block(feed, 'environment');
  const capabilitiesBlock = block(feed, 'capabilities');
  const newsBlock = block(feed, 'news');
  const pathBlock = block(feed, 'pathOfLight');
  const pathSectionEnabled = isBlockEnabled(feed, 'pathOfLight');
  const profileReadyForUser = !!gamificationOwnerKey && gamificationState.ownerKey === gamificationOwnerKey;
  const lightPathProfile = profileReadyForUser ? gamificationState.profile : null;
  const lightPathState = resolveHomeLightPathState({
    sectionEnabled: pathSectionEnabled,
    authLoading,
    isAuthenticated,
    isParticipant: isGamificationParticipant,
    profileLoading: gamificationState.loading,
    profileReadyForUser,
    profile: lightPathProfile,
    profileError: profileReadyForUser ? gamificationState.error : null,
  });

  const liveNow = feed?.liveNow || [];
  const upcoming = useMemo(() => feed?.upcomingEvents || [], [feed?.upcomingEvents]);
  useEffect(() => {
    upcoming.forEach(item => {
      if (!isOfflineHomeEvent(item) || item.isAttending !== true) return;
      const ref = parseUnifiedEventRef(item.detailsUrl) || { source: 'master', id: item.id };
      seedOfflineEventRegistration(ref.id, true);
    });
  }, [upcoming, seedOfflineEventRegistration]);
  const homeFeaturedMasters = useMemo(() => feed?.featuredMasters || [], [feed?.featuredMasters]);
  const homeNewMasters = useMemo(() => feed?.newMasters || [], [feed?.newMasters]);

  useEffect(() => {
    setMasterSubscriptionOverrides(new Map());
    setPendingMasterFollows(new Set());
    setMasterFollowErrors(new Set());
  }, [masterOwnerKey]);

  const rankedMasterById = useMemo(
    () => new Map((rankedMasters || []).map((master) => [master.id, master])),
    [rankedMasters],
  );

  const performMasterFollow = useCallback(async (masterId: number, authenticatedUserId?: string | number) => {
    if (pendingMasterFollows.has(masterId)) return;
    const profile = rankedMasterById.get(masterId);
    const wasSubscribed = masterSubscriptionOverrides.has(masterId)
      ? masterSubscriptionOverrides.get(masterId) as boolean
      : profile?.isSubscribed === true;
    // Home offers follow only; subscription management stays in the profile.
    if (wasSubscribed) return;
    const nextSubscribed = true;
    const requestOwner = masterOwnerRef.current;
    const subscriptionUserId = authenticatedUserId ?? user?.id;
    const isCurrentOwner = () => masterOwnerRef.current === requestOwner
      || (subscriptionUserId != null && masterOwnerRef.current === `user:${subscriptionUserId}`);

    setPendingMasterFollows((current) => new Set(current).add(masterId));
    setMasterFollowErrors((current) => {
      const next = new Set(current);
      next.delete(masterId);
      return next;
    });
    setMasterSubscriptionOverrides((current) => new Map(current).set(masterId, nextSubscribed));

    try {
      const state = await api.subscribeToMaster(masterId);
      if (!isCurrentOwner()) return;
      const confirmedSubscribed = typeof state?.isSubscribed === 'boolean'
        ? state.isSubscribed
        : nextSubscribed;
      setMasterSubscriptionOverrides((current) => new Map(current).set(masterId, confirmedSubscribed));
      if (subscriptionUserId != null) {
        publishMasterSubscriptionChanged({
          userId: String(subscriptionUserId),
          masterId,
          isSubscribed: confirmedSubscribed,
        });
      }
    } catch {
      if (!isCurrentOwner()) return;
      setMasterSubscriptionOverrides((current) => new Map(current).set(masterId, wasSubscribed));
      setMasterFollowErrors((current) => new Set(current).add(masterId));
    } finally {
      if (isCurrentOwner()) setPendingMasterFollows((current) => {
        const next = new Set(current);
        next.delete(masterId);
        return next;
      });
    }
  }, [masterSubscriptionOverrides, pendingMasterFollows, rankedMasterById, user?.id]);

  // Explicit account actions keep the return destination on the homepage.
  const handleGuestRegister = useCallback(() => {
    navigation.navigate('Register', { returnTo: '/' });
  }, [navigation]);
  const handleGuestLogin = useCallback(() => {
    navigation.navigate('Login', { returnTo: '/' });
  }, [navigation]);

  const handleMasterFollow = useCallback((masterId: number) => {
    if (!isAuthenticated) {
      showAuthCTA(
        'gated_action',
        labels.followAuth,
        (authenticatedUser) => void performMasterFollow(masterId, authenticatedUser?.id),
        { returnTo: '/' },
      );
      return;
    }
    void performMasterFollow(masterId);
  }, [isAuthenticated, labels.followAuth, performMasterFollow, showAuthCTA]);

  const featuredMasters = useMemo<HomeMasterItem[]>(
    () => resolveFeaturedHomeMasters(homeFeaturedMasters, rankedMasters || [], langKey),
    [homeFeaturedMasters, langKey, rankedMasters],
  );
  const newMasters = useMemo<HomeMasterItem[]>(
    () => mergeHomeMasters(enrichHomeMastersWithProfiles(homeNewMasters, rankedMasters || [], langKey)),
    [homeNewMasters, langKey, rankedMasters],
  );
  const communities = feed?.communities || [];
  const newsItems = useMemo(() => buildNewsArticles(feed), [feed]);
  const showLiveNow = isBlockEnabled(feed, 'liveNowHeader');
  const showUpcoming = isBlockEnabled(feed, 'upcomingEventsHeader');
  const showFeaturedMasters = isBlockEnabled(feed, 'featuredMastersHeader');
  const showNewMasters = isBlockEnabled(feed, 'newMastersHeader');
  const featuredMastersLoading = loading
    || (!!feed && homeFeaturedMasters.length === 0 && rankedMasters === null);
  const featuredMastersError = featuredMasters.length > 0
    ? null
    : sectionError(feed, 'featured_masters');
  const shouldShowLiveNow = showLiveNow && (loading || liveNow.length > 0);
  const hasDesktopContentGrid = width >= 1024;
  const contentGridColumns = isDesktop ? 3 : isMobile ? 1 : 2;
  const capabilityGridColumns = hasDesktopContentGrid ? 4 : isMobile ? 1 : 2;
  const masterGridColumns = isDesktop ? 4 : isMobile ? 2 : 4;
  const handleHowOpen = (url: string | null | undefined) => {
    trackCtaClicked({ cta: MarketingActions.homeLearnMore, authState, section: 'how_it_works' });
    navigateToUrl(url);
  };

  const openLightPathTab = useCallback(() => {
    navigation.navigate('Dashboard', { initialTab: 'lightPath' });
  }, [navigation]);

  const handleLightPathPrimary = useCallback((state: HomeLightPathRenderState) => {
    if (state === 'hidden' || state === 'loading') return;
    trackCtaClicked({
      cta: state === 'guest'
        ? 'light_path_start'
        : state === 'error'
          ? 'light_path_open_no_metrics'
          : 'light_path_primary',
      authState,
      section: 'light_path',
    });
    if (!isAuthenticated) {
      showAuthCTA('gated_action', labels.authLightPath, openLightPathTab, {
        heading: labels.authLightPathHeading,
        homeSource: 'light_path',
        returnTo: '/dashboard?initialTab=lightPath',
        // CORE-1277 — это и есть CTA «Начать путь» из инструкции Adventum:
        // гость с него уходит в регистрацию, и цель воронки считает тех, кто
        // её дошёл до конца.
        signupFunnel: SignupSources.startPath,
      });
      return;
    }
    openLightPathTab();
  }, [authState, isAuthenticated, labels.authLightPath, labels.authLightPathHeading, openLightPathTab, showAuthCTA]);

  const handleLiveJoin = (item: HomeLiveNowItem, position: number) => {
    const entityId = String(item.id);
    trackLiveJoinClicked({ entityId, authState, position });
    if (!isAuthenticated || item.authRequired) {
      trackActionResult({ action: 'live_join', result: 'soft_auth', entityId });
      showAuthCTA('gated_action', labels.authLive, () => navigateToUrl(item.joinUrl || `/live/${item.id}`), { homeSource: 'live_join' });
      return;
    }
    trackActionResult({ action: 'live_join', result: 'success', entityId });
    navigateToUrl(item.joinUrl || `/live/${item.id}`);
  };

  const handleEventRsvp = async (item: HomeEventItem, position: number) => {
    const entityId = String(item.id);
    const registrationKey = homeEventRegistrationKey(item);
    const liveEventId = resolveHomeLiveEventId(item);
    trackEventRsvpClicked({ entityId, authState, position });

    if (isOfflineHomeEvent(item)) {
      const ref = parseUnifiedEventRef(item.detailsUrl) || { source: 'master', id: item.id };
      if (!isAuthenticated) {
        trackActionResult({ action: 'event_rsvp', result: 'soft_auth', entityId });
        showAuthCTA('gated_action', labels.authEvent, () => navigateToUrl(item.detailsUrl || `/lives?eventId=${ref.id}`), { homeSource: 'event_rsvp' });
        return;
      }
      try {
        await toggleOfflineEventRegistration({ id: ref.id });
        trackActionResult({ action: 'event_rsvp', result: 'success', entityId });
      } catch (err: any) {
        trackActionResult({ action: 'event_rsvp', result: 'error', entityId, errorCode: err?.code || err?.status || 'RSVP_FAILED' });
      }
      return;
    }

    // CORE-1286 — у комнаты нет RSVP на этой поверхности: её «иду» — подписка
    // на комнату, а не LiveSubscription. Бэкенд шлёт canRsvp=false, но гость
    // видит кнопку до входа (authRequired), и без этой ветки продолжение после
    // логина зарегистрировало бы его на мастер-ивент с номером комнаты.
    const roomCode = parseHomeRoomCode(item.detailsUrl);
    if (roomCode) {
      if (!isAuthenticated) {
        trackActionResult({ action: 'event_rsvp', result: 'soft_auth', entityId });
        showAuthCTA('gated_action', labels.authEvent, () => navigateToUrl(item.detailsUrl), { homeSource: 'event_rsvp' });
        return;
      }
      // CORE-1428 — вошедшему кнопка тоже должна что-то делать. До этой ветки
      // она молчала, и именно поэтому её было проще всего погасить в
      // «Недоступно», чем признать, что нажимать не на что.
      trackActionResult({ action: 'event_rsvp', result: 'success', entityId });
      navigateToUrl(item.detailsUrl);
      return;
    }

    const performRsvp = async () => {
      if (item.isAttending === true || eventRsvpSuccessKeysRef.current.has(registrationKey)) return;
      if (eventRsvpPendingKeysRef.current.has(registrationKey)) return;
      const requestSession = { ...eventRsvpSessionRef.current };
      const isCurrentSession = () => (
        eventRsvpSessionRef.current.ownerKey === requestSession.ownerKey
        && eventRsvpSessionRef.current.generation === requestSession.generation
      );
      eventRsvpPendingKeysRef.current.add(registrationKey);
      setEventRsvpPendingKeys(prev => new Set(prev).add(registrationKey));
      setEventRsvpErrorByKey(prev => {
        if (!prev.has(registrationKey)) return prev;
        const next = new Map(prev);
        next.delete(registrationKey);
        return next;
      });

      const markSuccess = () => {
        if (!isCurrentSession()) return;
        eventRsvpSuccessKeysRef.current.add(registrationKey);
        setEventRsvpSuccessKeys(prev => new Set(prev).add(registrationKey));
        trackActionResult({ action: 'event_rsvp', result: 'success', entityId });
      };

      try {
        if (liveEventId != null) {
          await addToCalendar(liveEventId);
        } else {
          const ref = resolveHomeEventRegistrationRef(item);
          await api.registerForEvent(ref.source, ref.id);
        }
        markSuccess();
      } catch (err: any) {
        // The unified API uses 409 both for a full event and for a stale client
        // that is already registered. Re-read the authoritative event before
        // deciding whether this is success or an error visible on the card.
        if (liveEventId == null && err?.status === 409) {
          try {
            const ref = resolveHomeEventRegistrationRef(item);
            const refreshedEvent = await api.getEvent(ref.source, ref.id);
            if (refreshedEvent.isRegistered) {
              markSuccess();
              return;
            }
          } catch {
            // Fall through to the localized retry message below.
          }
        }
        if (!isCurrentSession()) return;
        setEventRsvpErrorByKey(prev => new Map(prev).set(
          registrationKey,
          err?.status === 409 ? labels.rsvpUnavailable : labels.rsvpError,
        ));
        trackActionResult({ action: 'event_rsvp', result: 'error', entityId, errorCode: err?.code || err?.status || 'RSVP_FAILED' });
      } finally {
        if (isCurrentSession()) {
          eventRsvpPendingKeysRef.current.delete(registrationKey);
          setEventRsvpPendingKeys(prev => {
            const next = new Set(prev);
            next.delete(registrationKey);
            return next;
          });
        }
      }
    };

    if (!isAuthenticated) {
      trackActionResult({ action: 'event_rsvp', result: 'soft_auth', entityId });
      // Preserve the existing live continuation: after auth it opens the live
      // surface, whose current CalendarContext owns the add/remove state. The
      // unified online/offline action is safe to resume directly through API.
      const resumeAfterAuth = liveEventId != null
        ? () => navigateToUrl(item.detailsUrl || `/lives?eventId=${liveEventId}`)
        : () => { void performRsvp(); };
      showAuthCTA('gated_action', labels.authEvent, resumeAfterAuth, { homeSource: 'event_rsvp' });
      return;
    }
    if (item.canRsvp === false) return;
    await performRsvp();
  };

  const handleUpcomingOpen = async (item: HomeEventItem, position: number) => {
    trackCardClicked({ section: 'upcoming_events', entityType: 'event', entityId: String(item.id), position });
    if (isOfflineHomeEvent(item)) {
      const ref = parseUnifiedEventRef(item.detailsUrl) || { source: 'master', id: item.id };
      try {
        const event = await api.getEvent(ref.source, ref.id);
        setSelectedOfflineEvent(event);
      } catch (err: any) {
        trackDataLoadFailed({ section: 'upcoming_events', errorCode: err?.code || err?.status || 'OFFLINE_EVENT_LOAD_FAILED' });
        navigateToUrl(item.detailsUrl || `/events/master/${item.id}`);
      }
      return;
    }
    // CORE-1286 — «Live-сессия» мастера может быть комнатой (WatchParties), и
    // тогда её адрес — инвайт-код, а не номер. Откат на item.id ниже открыл бы
    // LiveDetail с id комнаты: пространства идентификаторов независимы, и там
    // либо пусто, либо ЧУЖАЯ сессия с тем же номером.
    const roomCode = parseHomeRoomCode(item.detailsUrl);
    if (roomCode) {
      navigation.navigate('RoomAnnouncement', { roomId: roomCode });
      return;
    }
    const internal = normalizeInternalUrl(item.detailsUrl);
    const [path] = (internal || '').split(/[?#]/);
    const liveId = path ? readNumericSegment(path, [/^\/live\/(\d+)\/?$/, /^\/lives\/(\d+)\/?$/]) : null;
    navigation.navigate('LiveDetail', { id: liveId ?? item.id });
  };

  const renderIntentionsPanel = (compact: boolean) => (
    <View style={[
      styles.intentionsPanel,
      compact ? styles.intentionsPanelMobile : styles.intentionsPanelDesktop,
      !compact && { width: desktopFlowerWidth, maxWidth: desktopFlowerWidth, flexBasis: 'auto' },
    ]}>
      <View style={[
        styles.intentionsHeading,
        !compact && styles.intentionsHeadingDesktop,
        !compact && { width: desktopFlowerWidth, maxWidth: desktopFlowerWidth, marginBottom: 24 },
      ]}>
        <Text style={[
          styles.intentionsHeadingTitle,
          !compact && styles.intentionsHeadingTitleDesktop,
          { color: isDark ? '#F9F3E5' : '#314C48', fontFamily: 'var(--ag-font-brand)', fontSize: compact ? 22 : 22 * desktopHeroScale, lineHeight: compact ? 29 : 29 * desktopHeroScale, textAlign: compact ? 'left' : 'center', alignSelf: 'stretch' },
        ]}>
          {labels.intentionsTitle}
        </Text>
      </View>
      <View style={!compact ? { width: desktopFlowerWidth, height: desktopFlowerHeight } : { width: '100%' }}>
      <View style={!compact ? {
        width: 534,
        height: 416,
        transform: [{ scale: desktopHeroScale }],
        ...(Platform.OS === 'web' ? { transformOrigin: 'top left' } : {}),
      } : { width: '100%' }}>
      <HomeDirections
        roulette={roulette}
        items={intentionsByLanguage[langKey]}
        isDark={isDark}
        isMobile={compact}
        language={langKey}
        placements={DESKTOP_PETAL_PLACEMENT}
        onAssistant={askFlowerAssistant}
        renderFlower={(highlighted, selected) => <DesktopLotusSkeleton activePetal={highlighted} selectedPetal={selected} isDark={isDark} />}
        onDirection={(index) => {
          const slugs = intentionsByLanguage[langKey][index].slugs;
          const analyticsSlugs = slugs.map(practiceDirectionAnalyticsSlug);
          trackCtaClicked({ cta: `hero_direction_${analyticsSlugs.join('_')}`, authState, section: 'hero' });
          navigateToUrl(practiceDirectionsUrl(slugs));
        }}
      />
      </View>
      </View>
    </View>
  );

  const { title: heroTitle, subtitle: heroBenefits } = resolveHomeHeroCopy(langKey, heroBlock, {
    title: labels.heroFallbackTitle, subtitle: labels.heroFallbackSubtitle,
  });
  const pageBg = isDark ? '#202226' : '#FAF8EF';
  const heroBottomFadeBg = pageBg;

  return (
    <View style={[styles.screen, { backgroundColor: pageBg }]}>
      <Header />
      <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={styles.scrollContent}>
        {/* The shell gutter is 30 everywhere EXCEPT phones, where it drops to
            the discovery gutter (16). The «Как это работает» card already used
            that value — it breaks out of this shell to full width and re-pads
            itself with discoveryPageGutter — so on a phone it sat 28px wider
            than every section below it, and those sections had 28px less room
            for their content. Matching the two lines up the whole column.
            Tablet and desktop keep 30, untouched. */}
        <View
          testID="home-content-shell"
          style={[
            styles.page,
            { paddingTop: headerInset },
            width < 768 && { paddingHorizontal: discoveryPageGutter(width) },
          ]}
        >
          <TrackableSection
            section="hero"
            style={[
              styles.heroSection,
              Platform.OS === 'web' && styles.heroFullBleedWeb,
              Platform.OS === 'web' && { marginTop: -headerInset },
            ]}
          >
            <DiscoveryHeroFrame
              testID="home-hero-viewport"
              copyTestID="home-hero-copy"
              // Keep all six full benefits visible when text wraps at any width.
              fitContent
              style={!isDesktop ? { paddingTop: heroArtworkHeight - 80 + headerInset } : { minHeight: Math.max(650, headerInset + heroArtworkHeight, desktopHeroTop + 58 * desktopHeroScale + 24 + desktopFlowerHeight + 32) }}
              railStyle={isDesktop ? { paddingTop: desktopHeroTop } : undefined}
              copyStyle={isDesktop ? {
                width: desktopCopyWidth,
                maxWidth: desktopCopyWidth,
              } : { width: '100%', maxWidth: 630, paddingBottom: 16, marginTop: 0 }}
              asideStyle={isDesktop ? {
                width: desktopFlowerWidth,
                top: desktopHeroTop,
              } : undefined}
              titleStyle={{ color: isDark ? '#F9F3E5' : '#314C48', fontSize: isDesktop ? 42 * desktopHeroScale : 28, lineHeight: isDesktop ? 52 * desktopHeroScale : 36 }}
              title={heroTitle}
              subtitle={heroBenefits}
              subtitleContent={(
                <HeroBenefits
                  subtitle={heroBenefits}
                  isDark={isDark}
                />
              )}
              isDark={isDark}
              background={(
                <View testID="home-hero-landscape" style={{ position: 'absolute', top: headerInset, left: 0, right: 0, height: heroArtworkHeight, overflow: 'hidden' }}>
                  <Image source={isDark ? heroLandscapeDarkImage : heroLandscapeLightImage} resizeMode="contain" style={{ position: 'absolute', top: 0, left: (width - heroArtworkWidth) / 2, width: heroArtworkWidth, height: heroImageHeight }} />
                  <LinearGradient colors={['transparent', heroBottomFadeBg]} locations={isDesktop ? [0.86, 1] : [0.76, 1]} style={StyleSheet.absoluteFillObject} />
                </View>
              )}
              aside={isDesktop ? (
                <View style={{ width: desktopFlowerWidth }}>
                  {renderIntentionsPanel(false)}
                </View>
              ) : undefined}
            />
          </TrackableSection>

          {!isDesktop && renderIntentionsPanel(true)}

          {/* CORE-1448 — разговор до регистрации. Стоит сразу после героя и
              перед «как это устроено»: человеку, который ещё не понял, что
              здесь есть, живой ответ объясняет быстрее описания. Гостю и
              только в вебе — внутри секции свои проверки. */}
          <View testID="home-info-cards" style={styles.infoCards}>
            {!isAuthenticated ? (
              <GuestAgapellaSection isDark={isDark} onRegister={handleGuestRegister} onLogin={handleGuestLogin} openRequest={assistantOpenRequest} />
            ) : null}
            <HowItWorksCtaSection isDark={isDark} onOpen={handleHowOpen} />
          </View>

          {/* CORE-1548 — приложение в сторах. Сначала стоял перед «как это
              устроено», по просьбе ПМ (17.10.2026) опущен под него. Только в
              вебе — в приложении звать в стор незачем. На телефоне блок ниже
              первого экрана, поэтому с баннером CORE-1116 в шапке один экран
              не делит. */}
          {Platform.OS === 'web' ? (
            <TrackableSection section="app_download">
              <AppDownloadSection isDark={isDark} authState={authState} />
            </TrackableSection>
          ) : null}

          {pageError ? (
            <View style={[styles.pageError, cardShell(isDark)]}>
              <Ionicons name="warning-outline" size={22} color={ACCENT_TEXT} />
              <Text style={[styles.pageErrorText, { color: isDark ? '#F3ECE3' : '#332720' }]}>{labels.loadError}</Text>
              <Button variant="convex-fire" size="sm" onPress={load}>{labels.retry}</Button>
            </View>
          ) : null}

          {shouldShowLiveNow ? (
            <CompactEventsSection
              section="live_now"
              title={liveHeader?.title || labels.liveNow}
              subtitle={liveHeader?.subtitle || labels.liveNowSubtitle}
              actionLabel={labels.seeAll}
              onAction={() => navigation.navigate('Lives')}
              isDark={isDark}
              loading={loading}
              error={sectionError(feed, 'live_now')}
              emptyText={labels.emptyLive}
              items={liveNow}
              columns={contentGridColumns}
              renderItem={(item, index) => (
                <LiveNowCard
                  // CORE-1286 — в «эфире» тоже две таблицы: адрес уникален, id нет.
                  key={item.joinUrl || item.id}
                  item={item}
                  position={index}
                  labels={labels}
                  isDark={isDark}
                  onOpen={() => {
                    trackCardClicked({ section: 'live_now', entityType: 'live', entityId: String(item.id), position: index });
                    navigateToUrl(item.joinUrl || `/live/${item.id}`);
                  }}
                  onJoin={() => handleLiveJoin(item, index)}
                />
              )}
            />
          ) : null}

          {showUpcoming ? (
            <CompactEventsSection
              section="upcoming_events"
              title={upcomingHeader?.title || labels.upcoming}
              subtitle={upcomingHeader?.subtitle || labels.upcomingSubtitle}
              actionLabel={labels.seeAll}
              onAction={() => {
                // CORE-1277 — цель «Клик "Календарь" из блока событий». Надпись
                // кнопки («Смотреть все» на проде, «Календарь» в инструкции
                // агентства) целью не управляет: она из локальных строк, а
                // шапка блока приходит из админки и переписывается редактором.
                trackCtaClicked({
                  cta: MarketingActions.homeEventsCalendar,
                  authState,
                  section: 'upcoming_events',
                });
                navigation.navigate('Lives');
              }}
              isDark={isDark}
              loading={loading}
              error={sectionError(feed, 'upcoming_events')}
              emptyText={labels.emptyUpcoming}
              items={upcoming}
              columns={contentGridColumns}
              renderItem={(item, index) => {
                const registrationKey = homeEventRegistrationKey(item);
                const liveEventId = resolveHomeLiveEventId(item);
                const offlineRef = isOfflineHomeEvent(item)
                  ? (parseUnifiedEventRef(item.detailsUrl) || { source: 'master', id: item.id })
                  : null;
                const isGoingNow = offlineRef
                  ? offlineRegistrationIds.has(offlineRef.id)
                  : item.isAttending === true || eventRsvpSuccessKeys.has(registrationKey)
                    || (liveEventId != null && reminderIds.has(liveEventId));
                // CORE-1428 — решение о кнопке вынесено целиком (см.
                // `upcomingCardAction.ts`): «нельзя записаться ЭТОЙ кнопкой» и
                // «событие недоступно» больше не одно и то же.
                const action = resolveUpcomingCardAction({
                  detailsUrl: item.detailsUrl,
                  canRsvp: item.canRsvp,
                  authRequired: item.authRequired,
                  isAuthenticated,
                  isGoing: isGoingNow,
                });
                return (
                  <UpcomingEventCard
                    // CORE-1286 — ключ по `${type}-${id}` схлопывал комнату и
                    // лайв с одинаковым номером в одну карточку вместе с их
                    // состоянием «иду»; registrationKey разводит их источники.
                    key={registrationKey}
                    item={item}
                    position={index}
                    locale={locale}
                    labels={labels}
                    isDark={isDark}
                    isGoing={isGoingNow}
                    canCancel={!!offlineRef}
                    isPending={offlineRef ? offlineRegistrationPendingIds.has(offlineRef.id) : eventRsvpPendingKeys.has(registrationKey)}
                    isUnavailable={action.kind === 'unavailable'}
                    // CORE-1428 — у комнаты кнопка ведёт на её страницу, и
                    // называться должна тем, что делает.
                    actionLabel={action.kind === 'open-room' ? labels.open : labels.rsvp}
                    rsvpError={offlineRef && offlineRegistrationErrorIds.has(offlineRef.id)
                      ? (langKey === 'ru' ? 'Не удалось изменить участие. Попробуйте ещё раз.' : 'Could not update RSVP. Please try again.')
                      : eventRsvpErrorByKey.get(registrationKey) || null}
                    onOpen={() => handleUpcomingOpen(item, index)}
                    onRsvp={() => handleEventRsvp(item, index)}
                  />
                );
              }}
            />
          ) : null}

          {showFeaturedMasters || showNewMasters ? (
            <View style={[styles.masterBlocks, isDesktop ? styles.masterBlocksDesktop : styles.masterBlocksMobile]}>
              {showFeaturedMasters ? (
                <MasterStrip
                  section="featured_masters"
                  title={featuredHeader?.title || labels.featuredMasters}
                  fallbackMeta={labels.master}
                  detailsLabel={labels.details}
                  followLabel={labels.follow}
                  followingLabel={labels.following}
                  followErrorLabel={labels.followError}
                  actionLabel={labels.seeAll}
                  onAction={() => navigation.navigate('Masters', { freshAt: Date.now() })}
                  masters={featuredMasters}
                  columns={masterGridColumns}
                  carousel={isMobile}
                  loading={featuredMastersLoading}
                  error={featuredMastersError}
                  emptyText={labels.emptyMasters}
                  isDark={isDark}
                  masterProfiles={rankedMasterById}
                  subscriptionOverrides={masterProfilesReady ? masterSubscriptionOverrides : new Map()}
                  pendingFollows={pendingMasterFollows}
                  subscriptionsLoading={isAuthenticated && !masterProfilesReady}
                  followErrors={masterFollowErrors}
                  onFollow={handleMasterFollow}
                  onOpen={(master, index) => {
                    trackCardClicked({ section: 'featured_masters', entityType: 'master', entityId: String(master.id), position: index });
                    navigateToUrl(master.profileUrl || `/masters/${master.id}`);
                  }}
                />
              ) : null}
              {showNewMasters ? (
                <MasterStrip
                  section="new_masters"
                  title={newHeader?.title || labels.newMasters}
                  fallbackMeta={labels.master}
                  detailsLabel={labels.details}
                  followLabel={labels.follow}
                  followingLabel={labels.following}
                  followErrorLabel={labels.followError}
                  actionLabel={labels.seeAll}
                  onAction={() => navigation.navigate('Masters', { freshAt: Date.now() })}
                  masters={newMasters}
                  columns={masterGridColumns}
                  carousel={isMobile}
                  loading={loading}
                  error={sectionError(feed, 'new_masters')}
                  emptyText={labels.emptyMasters}
                  isDark={isDark}
                  masterProfiles={rankedMasterById}
                  subscriptionOverrides={masterProfilesReady ? masterSubscriptionOverrides : new Map()}
                  pendingFollows={pendingMasterFollows}
                  subscriptionsLoading={isAuthenticated && !masterProfilesReady}
                  followErrors={masterFollowErrors}
                  onFollow={handleMasterFollow}
                  onOpen={(master, index) => {
                    trackCardClicked({ section: 'new_masters', entityType: 'master', entityId: String(master.id), position: index });
                    navigateToUrl(master.profileUrl || `/masters/${master.id}`);
                  }}
                />
              ) : null}
            </View>
          ) : null}

          {communities.length > 0 && isFeatureEnabled(feed, 'communities') && isBlockEnabled(feed, 'communities') ? (
            <CommunitiesSection
              communities={communities}
              labels={labels}
              loading={loading}
              error={sectionError(feed, 'communities')}
              isDark={isDark}
              columns={contentGridColumns}
              onOpen={(community, index) => {
                trackCardClicked({ section: 'communities', entityType: 'community', entityId: String(community.id), position: index });
                navigateToUrl(community.profileUrl || '/community');
              }}
            />
          ) : null}

          {isBlockEnabled(feed, 'environment') ? (
            <EnvironmentSection
              block={environmentBlock}
              labels={labels}
              isDark={isDark}
              onOpen={(url, index) => {
                trackCardClicked({ section: 'environment', entityType: 'capability', entityId: url || String(index), position: index });
                navigateToUrl(url);
              }}
            />
          ) : null}

          {isBlockEnabled(feed, 'news') ? (
            <NewsSection
              news={newsItems}
              title={newsBlock?.title || labels.news}
              labels={labels}
              loading={loading}
              error={sectionError(feed, 'news')}
              isDark={isDark}
              columns={contentGridColumns}
              onOpen={(item, index) => {
                trackCardClicked({ section: 'news', entityType: 'news', entityId: item.path || item.title || String(index), position: index });
                navigation.navigate('NewsArticle', { slug: item.slug });
              }}
              onSeeAll={() => navigation.navigate('News')}
            />
          ) : null}

          {isBlockEnabled(feed, 'capabilities') ? (
            <CapabilitiesSection
              block={capabilitiesBlock}
              labels={labels}
              isDark={isDark}
              columns={capabilityGridColumns}
              error={sectionError(feed, 'capabilities')}
              onOpen={(url, index) => {
                trackCardClicked({ section: 'capabilities', entityType: 'capability', entityId: url || String(index), position: index });
                navigateToUrl(url);
              }}
            />
          ) : null}

          {lightPathState !== 'hidden' ? (
            <LightPathSection
              block={pathBlock}
              labels={labels}
              isDark={isDark}
              isMobile={isMobile}
              state={lightPathState}
              profile={lightPathProfile}
              locale={locale}
              language={langKey}
              onPrimary={() => handleLightPathPrimary(lightPathState)}
            />
          ) : null}
        </View>
        <Footer />
      </ScrollView>
      <OfflineEventDetailsModal
        event={selectedOfflineEvent}
        isDark={isDark}
        language={langKey}
        onClose={() => setSelectedOfflineEvent(null)}
        themeColors={themeColors}
      />
    </View>
  );
};

const HomeEntityGrid: React.FC<{
  columns: number;
  dense?: boolean;
  children: React.ReactNode;
}> = ({ columns, dense = false, children }) => {
  const itemWidth = `${100 / Math.max(1, columns)}%`;

  return (
    <View style={[styles.entityGrid, dense && styles.entityGridDense]}>
      {React.Children.map(children, (child) => child == null ? null : (
        <View
          style={[
            styles.entityGridItem,
            dense && styles.entityGridItemDense,
            { width: itemWidth } as any,
          ]}
        >
          {child}
        </View>
      ))}
    </View>
  );
};

function CompactEventsSection<T>({
  section,
  title,
  subtitle,
  actionLabel,
  onAction,
  loading,
  error,
  emptyText,
  items,
  columns,
  renderItem,
  isDark,
}: {
  section: HomeSection;
  title: string;
  subtitle?: string | null;
  actionLabel?: string;
  onAction?: () => void;
  loading: boolean;
  error: string | null;
  emptyText: string;
  items: T[];
  columns: number;
  renderItem: (item: T, index: number) => React.ReactNode;
  isDark: boolean;
}) {
  const empty = !loading && !error && items.length === 0 ? emptyText : null;
  return (
    <TrackableSection section={section} style={[styles.framedSection, sectionShell(isDark)]}>
      <SectionHeader title={title} subtitle={subtitle} actionLabel={actionLabel} onAction={onAction} isDark={isDark} />
      {loading || error || empty ? (
        <SectionState loading={loading && items.length === 0} error={error} empty={empty} isDark={isDark} />
      ) : (
        <HomeEntityGrid columns={columns}>
          {items.slice(0, 3).map((item, index) => renderItem(item, index))}
        </HomeEntityGrid>
      )}
    </TrackableSection>
  );
}

const LiveNowCard: React.FC<{
  item: HomeLiveNowItem;
  position: number;
  labels: typeof copy.ru;
  isDark: boolean;
  onOpen: () => void;
  onJoin: () => void;
}> = ({ item, labels, isDark, onOpen, onJoin }) => {
  // CORE-1384 — вариант под размер карточки вместо оригинала: на проде здесь
  // качался снимок 7360 × 4912, чтобы показать его 132 точками.
  const cover = getFileUrl(pickImageVariant({
    variants: item.coverVariants,
    originalUrl: item.coverUrl,
    displayWidth: COVER_DISPLAY_WIDTH,
    pixelRatio: PixelRatio.get(),
  }) || pickImageVariant({
    variants: item.master?.photoVariants,
    originalUrl: item.master?.photoUrl,
    displayWidth: COVER_DISPLAY_WIDTH,
    pixelRatio: PixelRatio.get(),
  }));
  const avatar = getFileUrl(pickImageVariant({
    variants: item.master?.photoVariants,
    originalUrl: item.master?.photoUrl,
    displayWidth: HOST_AVATAR_DISPLAY_WIDTH,
    pixelRatio: PixelRatio.get(),
  }));
  const masterName = item.master?.name || labels.master;
  const placeholderColor = INITIALS_PALETTE[(item.master?.id || item.id) % INITIALS_PALETTE.length];
  return (
    <View style={[styles.eventCard, cardShell(isDark)]}>
      <TouchableOpacity activeOpacity={0.88} onPress={onOpen} style={styles.compactImageWrap} accessibilityRole="button">
        {cover ? (
          <Image source={{ uri: cover }} style={styles.compactImage} resizeMode="cover" />
        ) : (
          <View style={[styles.compactImage, styles.placeholder, { backgroundColor: placeholderColor }]}>
            <Text style={styles.placeholderText}>{initials(masterName)}</Text>
          </View>
        )}
        <View style={styles.liveBadge}>
          <Text style={styles.liveBadgeText}>LIVE</Text>
        </View>
      </TouchableOpacity>
      <View style={styles.eventBody}>
        <TouchableOpacity activeOpacity={0.88} onPress={onOpen} style={styles.eventTextHit} accessibilityRole="button">
          <Text style={[styles.eventTitle, { color: isDark ? '#F3ECE3' : '#332720' }]} numberOfLines={2}>
            {item.title || labels.liveNow}
          </Text>
          <Text style={[styles.eventMeta, { color: isDark ? '#B9B0B6' : '#625B67' }]} numberOfLines={1}>{masterName}</Text>
          <View style={styles.eventFooterRow}>
            {avatar ? <Image source={{ uri: avatar }} style={styles.inlineAvatar} /> : (
              <View style={[styles.inlineAvatar, styles.placeholder, { backgroundColor: placeholderColor }]}>
                <Text style={styles.inlineInitials}>{initials(masterName)}</Text>
              </View>
            )}
            <Text style={[styles.eventCount, { color: isDark ? '#A8A0A9' : '#686170' }]} numberOfLines={1}>
              {formatCount(item.joinedCount)} {labels.participants}
            </Text>
          </View>
        </TouchableOpacity>
        <TouchableOpacity onPress={onJoin} style={[styles.compactAction, { justifyContent: 'center' }]} activeOpacity={0.82} accessibilityRole="button">
          <Ionicons name="play" size={13} color={ON_ACCENT_TEXT} />
          <Text style={styles.compactActionText}>{labels.joinLive}</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const UpcomingEventCard: React.FC<{
  item: HomeEventItem;
  position: number;
  locale: string;
  labels: typeof copy.ru;
  isDark: boolean;
  isGoing: boolean;
  canCancel: boolean;
  isPending: boolean;
  isUnavailable: boolean;
  /** CORE-1428 — что написано на кнопке, когда она рабочая и человек ещё не идёт. */
  actionLabel: string;
  rsvpError: string | null;
  onOpen: () => void;
  onRsvp: () => void;
}> = ({ item, locale, labels, isDark, isGoing, canCancel, isPending, isUnavailable, actionLabel, rsvpError, onOpen, onRsvp }) => {
  const { width } = useWindowDimensions();
  const mobile = width < 768;
  const cover = getFileUrl(pickImageVariant({
    variants: item.coverVariants,
    originalUrl: item.coverUrl,
    displayWidth: COVER_DISPLAY_WIDTH,
    pixelRatio: PixelRatio.get(),
  }) || pickImageVariant({
    variants: item.host?.photoVariants,
    originalUrl: item.host?.photoUrl,
    displayWidth: COVER_DISPLAY_WIDTH,
    pixelRatio: PixelRatio.get(),
  }));
  const avatar = getFileUrl(pickImageVariant({
    variants: item.host?.photoVariants,
    originalUrl: item.host?.photoUrl,
    displayWidth: HOST_AVATAR_DISPLAY_WIDTH,
    pixelRatio: PixelRatio.get(),
  }));
  const hostName = item.host?.name || labels.master;
  const placeholderColor = INITIALS_PALETTE[(item.host?.id || item.id) % INITIALS_PALETTE.length];
  const kind = (item.type || '').toLowerCase();
  const kindLabel = kind === 'offline' ? 'Offline' : kind === 'online' ? 'Online' : 'Live';
  return (
    <View style={[styles.eventCard, cardShell(isDark)]}>
      <TouchableOpacity activeOpacity={0.88} onPress={onOpen} style={styles.compactImageWrap} accessibilityRole="button">
        {cover ? (
          <Image source={{ uri: cover }} style={styles.compactImage} resizeMode="cover" />
        ) : (
          <View style={[styles.compactImage, styles.placeholder, { backgroundColor: placeholderColor }]}>
            <Text style={styles.placeholderText}>{initials(hostName)}</Text>
          </View>
        )}
        <View style={[styles.formatBadge, kind === 'live' && styles.formatBadgeLive]}>
          <Text style={styles.formatBadgeText}>{kindLabel}</Text>
        </View>
      </TouchableOpacity>
      <View style={styles.eventBody}>
        <TouchableOpacity activeOpacity={0.88} onPress={onOpen} style={styles.eventTextHit} accessibilityRole="button">
          <Text style={[styles.eventDate, { color: isDark ? '#B9B0B6' : '#625B67' }]} numberOfLines={1}>
            {formatDateTime(item.startAt, locale)}
          </Text>
          <Text style={[styles.eventTitle, { color: isDark ? '#F3ECE3' : '#332720' }]} numberOfLines={2}>
            {item.title || labels.upcoming}
          </Text>
          <View style={styles.eventFooterRow}>
            {avatar ? <Image source={{ uri: avatar }} style={styles.inlineAvatar} /> : (
              <View style={[styles.inlineAvatar, styles.placeholder, { backgroundColor: placeholderColor }]}>
                <Text style={styles.inlineInitials}>{initials(hostName)}</Text>
              </View>
            )}
            <Text style={[styles.eventCount, { color: isDark ? '#A8A0A9' : '#686170' }]} numberOfLines={1}>
              {hostName}
            </Text>
          </View>
        </TouchableOpacity>
        <TouchableOpacity
          testID="home-event-action"
          onPress={onRsvp}
          disabled={(!canCancel && isGoing) || isPending || isUnavailable}
          style={[styles.compactActionHit, { width: mobile ? 138 : 180 }]}
          activeOpacity={0.82}
          accessibilityRole="button"
          accessibilityLabel={isGoing ? labels.going : isUnavailable ? labels.rsvpUnavailable : actionLabel}
          accessibilityState={{ disabled: (!canCancel && isGoing) || isPending || isUnavailable, busy: isPending }}
        >
          <View testID="home-event-action-content" style={[
            styles.compactAction,
            { justifyContent: 'center' },
            { width: '100%', minHeight: mobile ? 36 : 48 },
            isGoing && styles.compactActionMuted,
            (isPending || isUnavailable) && styles.compactActionPending,
          ]}>
          {isPending ? (
            <ActivityIndicator size="small" color={ON_ACCENT_TEXT} />
          ) : (
            <Ionicons name={isGoing ? 'checkmark' : 'calendar-outline'} size={13} color={ON_ACCENT_TEXT} />
          )}
          <Text style={styles.compactActionText}>
            {isGoing ? labels.going : isUnavailable ? labels.rsvpUnavailable : actionLabel}
          </Text>
          </View>
        </TouchableOpacity>
        {rsvpError ? (
          <Text
            style={[styles.compactActionError, { color: isDark ? '#FFB6A3' : '#9D3E27' }]}
            accessibilityLiveRegion="polite"
            numberOfLines={2}
          >
            {rsvpError}
          </Text>
        ) : null}
      </View>
    </View>
  );
};

const MasterStrip: React.FC<{
  section: HomeSection;
  title: string;
  fallbackMeta: string;
  detailsLabel: string;
  followLabel: string;
  followingLabel: string;
  followErrorLabel: string;
  actionLabel: string;
  onAction: () => void;
  masters: HomeMasterItem[];
  columns: number;
  /** Mobile web: one card in full, the next peeking, instead of the grid (owner, 09.10.2026). */
  carousel?: boolean;
  loading: boolean;
  error: string | null;
  emptyText: string;
  isDark: boolean;
  masterProfiles: Map<number, PublicMasterProfile>;
  subscriptionOverrides: Map<number, boolean>;
  subscriptionsLoading?: boolean;
  pendingFollows: Set<number>;
  followErrors: Set<number>;
  onFollow: (masterId: number) => void;
  onOpen: (master: HomeMasterItem, index: number) => void;
}> = ({ section, title, fallbackMeta, detailsLabel, followLabel, followingLabel, followErrorLabel, actionLabel, onAction, masters, columns, carousel = false, loading, error, emptyText, isDark, masterProfiles, subscriptionOverrides, subscriptionsLoading = false, pendingFollows, followErrors, onFollow, onOpen }) => {
  const empty = !loading && !error && masters.length === 0 ? emptyText : null;
  const cards = masters.map((master, index) => (
    <MasterSmallCard
      key={master.id}
      master={master}
      fallbackMeta={fallbackMeta}
      detailsLabel={detailsLabel}
      followLabel={followLabel}
      followingLabel={followingLabel}
      followErrorLabel={followErrorLabel}
      isDark={isDark}
      isSubscribed={subscriptionOverrides.has(master.id)
        ? subscriptionOverrides.get(master.id) as boolean
        : masterProfiles.get(master.id)?.isSubscribed === true}
      isFollowPending={subscriptionsLoading || pendingFollows.has(master.id)}
      followError={followErrors.has(master.id)}
      onFollow={() => onFollow(master.id)}
      onOpen={() => onOpen(master, index)}
    />
  ));
  return (
    <TrackableSection section={section} style={[styles.masterStrip, sectionShell(isDark)]}>
      <SectionHeader title={title} actionLabel={actionLabel} onAction={onAction} compact isDark={isDark} />
      {loading || error || empty ? (
        <SectionState loading={loading && masters.length === 0} error={error} empty={empty} isDark={isDark} />
      ) : (
        carousel ? (
          <HomeCardCarousel inset={MASTER_STRIP_PADDING}>{cards}</HomeCardCarousel>
        ) : (
          <HomeEntityGrid columns={columns} dense>{cards}</HomeEntityGrid>
        )
      )}
    </TrackableSection>
  );
};

const MasterSmallCard: React.FC<{
  master: HomeMasterItem;
  fallbackMeta: string;
  detailsLabel: string;
  followLabel: string;
  followingLabel: string;
  followErrorLabel: string;
  isDark: boolean;
  isSubscribed: boolean;
  isFollowPending: boolean;
  followError: boolean;
  onFollow: () => void;
  onOpen: () => void;
}> = ({ master, fallbackMeta, detailsLabel: _detailsLabel, followLabel, followingLabel, followErrorLabel, isDark, isSubscribed, isFollowPending, followError, onFollow, onOpen }) => {
  const name = 'displayName' in master ? master.displayName : null;
  const avatar = getFileUrl(pickImageVariant({
    variants: 'avatarVariants' in master ? master.avatarVariants : null,
    // CORE-1384 — карточка приходит двумя путями: из `/api/home` с набором и из
    // публичного каталога, где до agape-backend!728 была одна ссылка на превью.
    // Второй путь и давал возврат QA 03.10: без набора карточка брала оригинал.
    previewUrl: 'avatarPreviewUrl' in master ? master.avatarPreviewUrl : null,
    originalUrl: 'avatarUrl' in master ? master.avatarUrl : null,
    displayWidth: MASTER_CARD_DISPLAY_WIDTH,
    pixelRatio: PixelRatio.get(),
  }));
  const meta = resolveHomeMasterCardMeta(master, fallbackMeta);
  const metaLabel = formatHomeMasterCardMeta(meta, fallbackMeta);
  const [shareOpen, setShareOpen] = useState(false);
  const profilePath = normalizeInternalUrl(master.profileUrl);
  const sharePath = profilePath && /^\/(?:u|masters)\/\d+(?:[/?#]|$)/u.test(profilePath) ? profilePath : `/masters/${master.id}`;
  const shareUrl = new URL(sharePath, getShareOrigin()).toString();
  const shareTitle = name || 'Agapella';
  const openShare = () => { void shareOrOpenModal(
    { url: shareUrl, title: shareTitle },
    { entityType: 'master', entityId: master.id, surface: 'home' },
    () => setShareOpen(true),
  ); };
  const placeholderColor = INITIALS_PALETTE[master.id % INITIALS_PALETTE.length];
  const imageFadeColor = isDark ? 'rgba(32,29,38,0.68)' : 'rgba(253,248,243,0.62)';
  return (
    <View style={[styles.masterCard, cardShell(isDark)]}>
      <TouchableOpacity
        onPress={onOpen}
        activeOpacity={0.88}
        style={styles.masterPrimaryAction}
        accessibilityRole="link"
        accessibilityLabel={`${name || 'Agapella'}. ${metaLabel}`}
      >
      <View style={[styles.masterImageWrap, { backgroundColor: imageFadeColor }]}>
        {avatar ? (
          <Image source={{ uri: avatar }} style={styles.masterImage} resizeMode="cover" />
        ) : (
          <View style={[styles.masterImage, styles.placeholder, { backgroundColor: placeholderColor }]}>
            <Text style={styles.placeholderText}>{initials(name)}</Text>
          </View>
        )}
      </View>
      <View style={styles.masterBody}>
        <Text style={[styles.masterName, { color: isDark ? '#F3ECE3' : '#332720' }]} numberOfLines={2}>{name || 'Agapella'}</Text>
        <Text style={[styles.masterDirection, { color: isDark ? '#B9B0B6' : '#625B67' }]} numberOfLines={2} ellipsizeMode="tail">
          {master.primaryDirection || metaLabel}
        </Text>
        {followError ? (
          <Text style={styles.masterFollowError} accessibilityLiveRegion="polite" numberOfLines={1}>{followErrorLabel}</Text>
        ) : null}
      </View>
      </TouchableOpacity>
      <View style={[styles.masterCardFooter, { borderTopColor: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(74,47,34,0.08)' }]}>
        <TouchableOpacity
          testID={isSubscribed ? 'home-master-following' : 'home-master-follow'}
          onPress={isSubscribed ? undefined : onFollow}
          disabled={isFollowPending || isSubscribed}
          activeOpacity={0.72}
          style={[
            styles.masterFollowButton,
            isSubscribed ? styles.masterFollowButtonActive : styles.masterFollowButtonIdle,
            isSubscribed && { borderColor: isDark ? '#9AAA83' : '#A8B794', backgroundColor: isDark ? '#3A4E45' : '#E6EEDB' },
            isFollowPending && styles.masterFollowButtonPending,
          ]}
          accessibilityRole="button"
          accessibilityLabel={`${isSubscribed ? followingLabel : followLabel}: ${name || 'Agapella'}`}
          accessibilityState={{ busy: isFollowPending, disabled: isFollowPending || isSubscribed, selected: isSubscribed }}
        >
          {isFollowPending ? (
            <ActivityIndicator size="small" color={isSubscribed ? ON_ACCENT_TEXT : ACCENT_TEXT} />
          ) : (
            <Ionicons name={isSubscribed ? 'checkmark' : 'person-add-outline'} size={21} color={isSubscribed ? (isDark ? '#E8F1D6' : '#3F6250') : ACCENT_TEXT} />
          )}
        </TouchableOpacity>
        <TouchableOpacity onPress={openShare} accessibilityRole="button" accessibilityLabel={`Поделиться профилем: ${shareTitle}`} style={[styles.masterFollowButton, { borderColor: isDark ? '#756E63' : '#BDB5A4' }]}><Ionicons name="share-social-outline" size={21} color={isDark ? '#F9F3E5' : '#314C48'} /></TouchableOpacity>
      </View>
      <ShareModal visible={shareOpen} onClose={() => setShareOpen(false)} url={shareUrl} title={shareTitle} analytics={{ entityType: 'master', entityId: master.id, surface: 'home' }} />
    </View>
  );
};

const CommunitiesSection: React.FC<{
  communities: HomeCommunityItem[];
  labels: typeof copy.ru;
  loading: boolean;
  error: string | null;
  isDark: boolean;
  columns: number;
  onOpen: (community: HomeCommunityItem, index: number) => void;
}> = ({ communities, labels, loading, error, isDark, columns, onOpen }) => (
  <TrackableSection section="communities" style={[styles.framedSection, sectionShell(isDark)]}>
    <SectionHeader title={labels.communities} actionLabel={labels.seeAll} onAction={() => onOpen(communities[0], 0)} isDark={isDark} />
    {loading || error ? <SectionState loading={loading && communities.length === 0} error={error} isDark={isDark} /> : (
      <HomeEntityGrid columns={columns}>
        {communities.slice(0, 3).map((community, index) => (
          <TouchableOpacity key={community.id} style={[styles.communityCard, cardShell(isDark)]} onPress={() => onOpen(community, index)}>
            {community.avatarUrl ? <Image source={{ uri: getFileUrl(community.avatarUrl)! }} style={styles.communityImage} /> : null}
            <Text style={[styles.communityName, { color: isDark ? '#F3ECE3' : '#332720' }]} numberOfLines={1}>{community.name}</Text>
            <Text style={[styles.communityText, { color: isDark ? '#B9B0B6' : '#625B67' }]} numberOfLines={2}>{community.shortDescription}</Text>
          </TouchableOpacity>
        ))}
      </HomeEntityGrid>
    )}
  </TrackableSection>
);

const EnvironmentSection: React.FC<{
  block: HomeContentBlock | null;
  labels: typeof copy.ru;
  isDark: boolean;
  onOpen: (url: string | null | undefined, index: number) => void;
}> = ({ block: data, labels, isDark, onOpen }) => {
  const blockCards = contentCards(data);
  const cards = blockCards.length > 0 ? blockCards : contentCtasAsCards(data);
  return (
    <TrackableSection section="environment" style={[styles.environment, sectionShell(isDark)]}>
      <Image source={environmentImage} style={styles.environmentImage} resizeMode="cover" />
      <View style={styles.environmentCopy}>
        <Text style={[styles.environmentTitle, { color: isDark ? '#F3ECE3' : '#332720' }]}>{data?.title || labels.environmentFallbackTitle}</Text>
        <Text style={[styles.environmentText, { color: isDark ? '#B9B0B6' : '#625B67' }]}>{data?.subtitle || labels.environmentFallbackSubtitle}</Text>
      </View>
      {cards.length > 0 ? (
        <View style={styles.environmentFacts}>
          {cards.map((card, index) => {
            const mediaUrl = getFileUrl(card.iconUrl || card.imageUrl || card.coverUrl);
            const canOpen = Boolean(card.url);
            return (
              <TouchableOpacity
                key={`${card.title || 'environment'}-${index}`}
                onPress={() => canOpen && onOpen(card.url, index)}
                activeOpacity={canOpen ? 0.82 : 1}
                disabled={!canOpen}
                style={[styles.environmentFact, cardShell(isDark)]}
                accessibilityRole={canOpen ? 'link' : undefined}
              >
                {mediaUrl ? (
                  <Image source={{ uri: mediaUrl }} style={styles.environmentFactImage} resizeMode="cover" />
                ) : (
                  <Ionicons name={CONTENT_CARD_FALLBACK_ICONS[index % CONTENT_CARD_FALLBACK_ICONS.length] as any} size={24} color={ACCENT_TEXT} />
                )}
                <View style={styles.environmentFactCopy}>
                  {card.title ? (
                    <Text style={[styles.environmentFactTitle, { color: isDark ? '#F3ECE3' : '#332720' }]} numberOfLines={1}>{card.title}</Text>
                  ) : null}
                  {card.text ? (
                    <Text style={[styles.environmentFactText, { color: isDark ? '#C7BEC5' : '#625B67' }]} numberOfLines={2}>{card.text}</Text>
                  ) : null}
                </View>
              </TouchableOpacity>
            );
          })}
        </View>
      ) : null}
    </TrackableSection>
  );
};

const NewsSection: React.FC<{
  news: NewsArticle[];
  title: string;
  labels: typeof copy.ru;
  loading: boolean;
  error: string | null;
  isDark: boolean;
  columns: number;
  onOpen: (item: NewsArticle, index: number) => void;
  onSeeAll: () => void;
}> = ({ news, title, labels, loading, error, isDark, columns, onOpen, onSeeAll }) => {
  const empty = !loading && !error && news.length === 0 ? labels.emptyNews : null;
  return (
    <TrackableSection section="news" style={[styles.framedSection, sectionShell(isDark)]}>
      <SectionHeader title={title} actionLabel={news.length ? labels.seeAll : undefined} onAction={news.length ? onSeeAll : undefined} isDark={isDark} />
      {loading || error || empty ? (
        <SectionState loading={loading && news.length === 0} error={error} empty={empty} isDark={isDark} />
      ) : (
        <HomeEntityGrid columns={columns}>
          {news.slice(0, 3).map((item, index) => {
            const imageUrl = getFileUrl(item.imageUrl);
            return (
              <TouchableOpacity key={`${item.title}-${index}`} onPress={() => onOpen(item, index)} style={[styles.newsCard, cardShell(isDark)]}>
                {imageUrl ? <Image source={{ uri: imageUrl }} style={styles.newsImage} resizeMode="cover" /> : null}
                <View style={styles.newsContent}>
                  <Text style={[styles.newsTitle, { color: isDark ? '#F3ECE3' : '#332720' }]} numberOfLines={2}>{item.title}</Text>
                  {item.description ? <Text style={[styles.newsText, { color: isDark ? '#B9B0B6' : '#625B67' }]} numberOfLines={2}>{item.description}</Text> : null}
                  <View style={styles.newsLinkRow}>
                    <Text style={styles.newsLink}>{labels.open}</Text>
                    <Ionicons name="arrow-forward" size={14} color={ACCENT_TEXT} />
                  </View>
                </View>
              </TouchableOpacity>
            );
          })}
        </HomeEntityGrid>
      )}
    </TrackableSection>
  );
};

const CapabilitiesSection: React.FC<{
  block: HomeContentBlock | null;
  labels: typeof copy.ru;
  isDark: boolean;
  columns: number;
  error: string | null;
  onOpen: (url: string | null | undefined, index: number) => void;
}> = ({ block: data, labels, isDark, columns, error, onOpen }) => {
  const cards = contentCards(data);

  return (
    <TrackableSection section="capabilities" style={[styles.framedSection, sectionShell(isDark)]}>
      <SectionHeader title={data?.title || labels.capabilities} subtitle={data?.subtitle} isDark={isDark} />
      {error ? <SectionState error={error} isDark={isDark} /> : cards.length > 0 ? (
        <HomeEntityGrid columns={columns}>
          {cards.map((card, index) => {
            const mediaUrl = getFileUrl(card.iconUrl || card.imageUrl || card.coverUrl);
            const canOpen = Boolean(card.url);
            return (
              <TouchableOpacity
                key={`${card.title || 'capability'}-${index}`}
                onPress={() => canOpen && onOpen(card.url, index)}
                activeOpacity={canOpen ? 0.82 : 1}
                disabled={!canOpen}
                style={[styles.capabilityCard, cardShell(isDark)]}
                accessibilityRole={canOpen ? 'link' : undefined}
              >
                <View style={[styles.capabilityIcon, { backgroundColor: isDark ? 'rgba(233,130,69,0.10)' : 'rgba(233,130,69,0.08)' }]}>
                  {mediaUrl ? (
                    <Image source={{ uri: mediaUrl }} style={styles.capabilityImage} resizeMode="cover" />
                  ) : (
                    <Ionicons name={CAPABILITY_ICONS[index % CAPABILITY_ICONS.length] as any} size={22} color={ACCENT_TEXT} />
                  )}
                </View>
                <View style={styles.capabilityTextBlock}>
                  <Text style={[styles.capabilityTitle, { color: isDark ? '#F3ECE3' : '#332720' }]} numberOfLines={2}>{card.title}</Text>
                  {card.text ? (
                    <Text style={[styles.capabilityText, { color: isDark ? '#B9B0B6' : '#625B67' }]} numberOfLines={3}>{card.text}</Text>
                  ) : null}
                </View>
              </TouchableOpacity>
            );
          })}
        </HomeEntityGrid>
      ) : null}
    </TrackableSection>
  );
};

const LightPathSection: React.FC<{
  block: HomeContentBlock | null;
  labels: typeof copy.ru;
  isDark: boolean;
  isMobile: boolean;
  state: HomeLightPathRenderState;
  profile: GamificationProfile | null;
  locale: string;
  language: keyof typeof copy;
  onPrimary: () => void;
}> = ({ labels, isMobile, state, onPrimary }) => (
  <TrackableSection section="light_path" style={styles.section}>
    <View testID="home-path-landscape" style={[styles.calmPath, isMobile && styles.calmPathMobile]}>
      {Platform.OS === 'web' ? (
        <img
          data-testid="home-path-artwork"
          src={Asset.fromModule(pathLandscapeImage).uri}
          loading="lazy"
          decoding="async"
          alt=""
          aria-hidden="true"
          draggable={false}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', borderRadius: 24, pointerEvents: 'none' }}
        />
      ) : (
        <Image source={pathLandscapeImage} resizeMode="cover" accessible={false} style={[StyleSheet.absoluteFillObject, { borderRadius: 24, width: '100%', height: '100%' }]} />
      )}
      {isMobile ? (
        <View pointerEvents="none" style={[StyleSheet.absoluteFillObject, { backgroundColor: 'rgba(255,249,230,0.62)' }]} />
      ) : (
        <LinearGradient pointerEvents="none" colors={['rgba(255,249,230,0.82)', 'rgba(255,249,230,0.60)', 'rgba(255,249,230,0.10)']} locations={[0, 0.38, 0.72]} start={{ x: 0, y: 0.5 }} end={{ x: 1, y: 0.5 }} style={StyleSheet.absoluteFillObject} />
      )}
      <View style={styles.calmPathCopy}>
        <Text testID="home-path-title" accessibilityRole="header" style={[styles.calmPathTitle, isMobile && { fontSize: 24, lineHeight: 32, textAlign: 'center' }]}>{labels.calmPathTitle}</Text>
        <Text style={[styles.calmPathBody, isMobile && { textAlign: 'center' }]}>{labels.calmPathBody}</Text>
      </View>
      {state === 'loading' ? <ActivityIndicator accessibilityLabel={labels.loading} color="#49685B" /> : (
        <Button testID="home-path-open" variant="outline" iconColor="#314C48" onPress={onPrimary} style={styles.calmPathButton} textStyle={{ color: '#314C48', fontSize: 15 }} showArrow>
          {/* No API flag currently distinguishes a started path. Do not infer it from authentication. */}
          {state === 'guest' ? labels.lightPathGuestCta : labels.lightPathOpen}
        </Button>
      )}
    </View>
  </TrackableSection>
);

const styles = StyleSheet.create({
  calmPath: { minHeight: 208, borderRadius: 24, overflow: 'hidden', padding: 36, gap: 24, flexDirection: 'row', alignItems: 'center', borderWidth: 1, borderColor: '#BCB89C' },
  calmPathMobile: { minHeight: 304, padding: 28, flexDirection: 'column', justifyContent: 'center' },
  calmPathCopy: { flex: 1, gap: 12 },
  calmPathTitle: { fontFamily: 'var(--ag-font-brand)', fontSize: 28, lineHeight: 37, color: '#314C48' },
  calmPathBody: { ...uiFont(), fontSize: 15, lineHeight: 23, color: '#49685B' },
  calmPathButton: { minHeight: 48, borderWidth: 1.5, borderColor: '#6F887B', borderRadius: 16, paddingHorizontal: 28, backgroundColor: 'rgba(255,253,229,0.7)' },
  screen: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 0,
  },
  page: {
    width: '100%',
    maxWidth: DISCOVERY_SHELL_MAX_WIDTH,
    alignSelf: 'center',
    paddingHorizontal: 30,
    paddingTop: 28,
    paddingBottom: 28,
    gap: 24,
  },
  infoCards: { gap: 24 },
  heroSection: {
    position: 'relative',
    width: '100%',
  },
  heroBenefits: {
    maxWidth: 520,
    gap: 8,
  },
  heroBenefitsLight: { position: 'relative', paddingHorizontal: 20, paddingVertical: 16 },
  heroBenefitsDark: { position: 'relative' },
  heroBenefitsHazeDark: { backgroundColor: 'rgba(32,34,38,0.675)', top: -12, bottom: -12, left: -20, right: -20 },
  heroBenefitsHaze: { ...StyleSheet.absoluteFillObject, backgroundColor: 'rgba(255,254,247,0.69)', borderRadius: 24, ...(Platform.OS === 'web' ? { filter: 'blur(24px)' } : {}) } as any,
  heroBenefitRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  heroBenefitSymbol: {
    width: 14,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  heroBenefitSymbolFill: {
    width: 10,
    height: 10,
    borderRadius: 5,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#E98245',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.28,
    shadowRadius: 4,
  },
  heroBenefitSymbolCore: {
    width: 3,
    height: 3,
    borderRadius: 1.5,
    backgroundColor: 'rgba(255,255,255,0.92)',
  },
  heroBenefitText: {
    ...typographyTokens.body,
    flex: 1,
  } as any,
  heroFullBleedWeb: {
    width: '100vw',
    maxWidth: 'none',
    marginLeft: 'calc(50% - 50vw)',
    marginRight: 'calc(50% - 50vw)',
  } as any,
  heroIntentionsCompact: {
    width: '100%',
    alignItems: 'center',
    paddingVertical: 24,
  },
  intentionsPanel: {
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: 534,
    width: 534,
    minWidth: 0,
    maxWidth: 534,
    alignItems: 'center',
  },
  intentionsPanelDesktop: {
    transform: [{ translateX: 0 }],
  },
  intentionsPanelMobile: {
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: 'auto',
    width: '100%',
    maxWidth: '100%',
  },
  intentionsHeading: {
    position: 'relative',
    zIndex: 2,
    width: '100%',
    maxWidth: 640,
    alignItems: 'center',
    marginBottom: 12,
  },
  intentionsHeadingDesktop: {
    width: 534,
    maxWidth: 534,
    alignSelf: 'center',
    marginBottom: 4,
  },
  intentionsHeadingTitle: {
    maxWidth: 610,
    textAlign: 'center',
    ...typographyTokens.uiH2,
  } as any,
  intentionsHeadingTitleDesktop: {
    width: '100%',
    maxWidth: 534,
  },
  pageError: {
    borderWidth: 1,
    borderRadius: HOME_BLOCK_RADIUS,
    padding: 18,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
  },
  pageErrorText: {
    flex: 1,
    ...typographyTokens.bodyAccent,
  } as any,
  section: {
    width: '100%',
  },
  framedSection: {
    width: '100%',
    borderWidth: 1,
    borderRadius: HOME_BLOCK_RADIUS,
    padding: 18,
    overflow: 'hidden',
  },
  sectionHeader: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 16,
    rowGap: 8,
    marginBottom: 14,
  },
  sectionHeaderCompact: {
    marginBottom: 12,
  },
  sectionTitleBlock: {
    flex: 1,
    minWidth: 180,
  },
  sectionTitle: {
    ...typographyTokens.uiH2,
  } as any,
  sectionSubtitle: {
    marginTop: 4,
    ...typographyTokens.body,
  } as any,
  sectionAction: {
    marginLeft: 'auto',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    minHeight: 32,
  },
  sectionActionText: {
    color: ACCENT_TEXT,
    ...typographyTokens.label,
  } as any,
  entityGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginHorizontal: -DISCOVERY_GRID_HALF_GAP,
    marginBottom: -24,
  },
  entityGridDense: {
    marginHorizontal: -7,
    marginBottom: -14,
  },
  entityGridItem: {
    paddingHorizontal: DISCOVERY_GRID_HALF_GAP,
    marginBottom: 24,
    alignSelf: 'stretch',
  },
  entityGridItemDense: {
    paddingHorizontal: 7,
    marginBottom: 14,
  },
  eventCard: {
    width: '100%',
    flexGrow: 1,
    minHeight: 132,
    borderWidth: 1,
    borderRadius: HOME_BLOCK_RADIUS,
    overflow: 'hidden',
    flexDirection: 'row',
  },
  compactImageWrap: {
    width: 132,
    minHeight: 132,
    overflow: 'hidden',
  },
  compactImage: {
    width: '100%',
    height: '100%',
  },
  liveBadge: {
    position: 'absolute',
    top: 10,
    left: 10,
    borderRadius: HOME_BLOCK_RADIUS,
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: '#F25C05',
  },
  liveBadgeText: {
    color: ON_ACCENT_TEXT,
    ...typographyTokens.captionAccent,
  } as any,
  formatBadge: {
    position: 'absolute',
    top: 10,
    left: 10,
    borderRadius: HOME_BLOCK_RADIUS,
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: 'rgba(0,0,0,0.62)',
  },
  formatBadgeLive: {
    backgroundColor: '#F25C05',
  },
  formatBadgeText: {
    color: ON_ACCENT_TEXT,
    ...typographyTokens.captionAccent,
  } as any,
  eventBody: {
    flex: 1,
    minWidth: 0,
    padding: 14,
    gap: 6,
  },
  eventTextHit: {
    minWidth: 0,
    gap: 6,
  },
  eventDate: {
    ...typographyTokens.captionAccent,
  } as any,
  eventTitle: {
    ...typographyTokens.bodyAccent,
  } as any,
  eventMeta: {
    ...typographyTokens.label,
  } as any,
  eventFooterRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    minHeight: 24,
  },
  inlineAvatar: {
    width: 22,
    height: 22,
    borderRadius: 11,
  },
  inlineInitials: {
    color: '#FFFFFF',
    ...typographyTokens.captionAccent,
  },
  eventCount: {
    flex: 1,
    ...typographyTokens.captionAccent,
  } as any,
  compactActionHit: { marginTop: 'auto', alignSelf: 'flex-start', minHeight: 44, maxWidth: '100%', justifyContent: 'center' },
  compactAction: {
    marginTop: 'auto',
    alignSelf: 'flex-start',
    minHeight: 30,
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 7,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#FF9756',
    borderWidth: 1,
    borderColor: '#F26A36',
    ...(Platform.OS === 'web' && {
      backgroundImage: 'linear-gradient(100deg, #FF9756 0%, #FFD3A6 100%)',
      boxShadow: '0 7px 20px rgba(244,81,34,0.20)',
    }),
  } as any,
  compactActionMuted: {
    backgroundColor: '#38A169',
  },
  compactActionPending: {
    opacity: 0.72,
  },
  compactActionError: {
    marginTop: 5,
    maxWidth: 210,
    ...typographyTokens.error,
  } as any,
  compactActionText: {
    color: '#3D352B',
    ...uiFont(700),
    fontSize: 14,
    lineHeight: 20,
  } as any,
  stateBox: {
    minHeight: 94,
    borderWidth: 1,
    borderRadius: HOME_BLOCK_RADIUS,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 18,
    gap: 8,
  },
  stateText: {
    ...typographyTokens.label,
    textAlign: 'center',
  } as any,
  masterBlocks: {
    gap: 24,
  },
  masterBlocksDesktop: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  masterBlocksMobile: {
    flexDirection: 'column',
  },
  masterStrip: {
    flex: 1,
    borderWidth: 1,
    borderRadius: HOME_BLOCK_RADIUS,
    padding: MASTER_STRIP_PADDING,
  },
  masterCard: {
    // Keep intrinsic content height in horizontal rails. WebKit can resolve
    // `flex: 1`'s zero basis before the image's aspect ratio is laid out,
    // leaving the body and actions outside the clipped ScrollView viewport.
    flexGrow: 1,
    flexShrink: 0,
    flexBasis: 'auto',
    width: '100%',
    borderRadius: HOME_BLOCK_RADIUS,
    borderWidth: 1,
    overflow: 'hidden',
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } as any : null),
  },
  masterImage: {
    width: '100%',
    height: '100%',
  },
  masterPrimaryAction: {
    flexGrow: 1,
    flexShrink: 0,
    flexBasis: 'auto',
  },
  masterImageWrap: {
    position: 'relative',
    width: '100%',
    aspectRatio: 1.13,
    overflow: 'hidden',
  },
  masterBody: {
    paddingHorizontal: 14,
    paddingTop: 14,
    paddingBottom: 10,
  },
  masterName: {
    ...typographyTokens.uiH3,
  } as any,
  masterDirection: {
    marginTop: 6,
    ...typographyTokens.body,
  } as any,
  masterFollowError: {
    marginTop: 6,
    color: '#B84D32',
    ...typographyTokens.error,
  } as any,
  masterCardFooter: {
    minHeight: 52,
    marginHorizontal: 14,
    paddingTop: 10,
    paddingBottom: 12,
    borderTopWidth: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 12,
  },
  masterDetailsAction: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  masterDetailsText: {
    ...typographyTokens.label,
  } as any,
  masterFollowButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  masterFollowButtonIdle: {
    borderColor: '#E98245',
    backgroundColor: 'transparent',
  },
  masterFollowButtonActive: {
    borderColor: '#E98245',
    backgroundColor: '#E98245',
  },
  masterFollowButtonPending: {
    opacity: 0.7,
  },
  environment: {
    borderWidth: 1,
    borderRadius: HOME_BLOCK_RADIUS,
    padding: 18,
    flexDirection: 'row',
    gap: 24,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  environmentImage: {
    width: 150,
    height: 150,
    borderRadius: 75,
  },
  environmentCopy: {
    flex: 1,
    minWidth: 260,
  },
  environmentTitle: {
    ...typographyTokens.uiH2,
  } as any,
  environmentText: {
    marginTop: 8,
    maxWidth: 520,
    ...typographyTokens.body,
  } as any,
  environmentFacts: {
    flex: 1.2,
    minWidth: 260,
    flexDirection: 'row',
    gap: 12,
    flexWrap: 'wrap',
  },
  environmentFact: {
    flex: 1,
    minWidth: 170,
    borderRadius: HOME_BLOCK_RADIUS,
    borderWidth: 1,
    padding: 14,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  environmentFactImage: {
    width: 34,
    height: 34,
    borderRadius: 17,
  },
  environmentFactCopy: {
    flex: 1,
    minWidth: 0,
  },
  environmentFactTitle: {
    ...typographyTokens.label,
  } as any,
  environmentFactText: {
    flex: 1,
    ...typographyTokens.label,
  } as any,
  newsCard: {
    width: '100%',
    flexGrow: 1,
    borderRadius: HOME_BLOCK_RADIUS,
    borderWidth: 1,
    padding: 18,
    minHeight: 118,
    overflow: 'hidden',
  },
  newsImage: {
    width: '100%',
    height: 126,
    borderRadius: HOME_BLOCK_RADIUS,
    marginBottom: 12,
  },
  newsContent: {
    flex: 1,
    minWidth: 0,
  },
  newsTitle: {
    ...typographyTokens.bodyAccent,
  } as any,
  newsText: {
    marginTop: 8,
    ...typographyTokens.body,
  } as any,
  newsLinkRow: {
    marginTop: 'auto',
    paddingTop: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
  },
  newsLink: {
    color: ACCENT_TEXT,
    ...typographyTokens.label,
  } as any,
  capabilityCard: {
    width: '100%',
    flexGrow: 1,
    borderWidth: 1,
    borderRadius: HOME_BLOCK_RADIUS,
    padding: 14,
    flexDirection: 'row',
    gap: 12,
    alignItems: 'center',
  },
  capabilityIcon: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  capabilityImage: {
    width: '100%',
    height: '100%',
  },
  capabilityTextBlock: {
    flex: 1,
    minWidth: 0,
  },
  capabilityTitle: {
    ...typographyTokens.label,
  } as any,
  capabilityText: {
    marginTop: 3,
    ...typographyTokens.caption,
  } as any,
  communityCard: {
    width: '100%',
    flexGrow: 1,
    borderRadius: HOME_BLOCK_RADIUS,
    borderWidth: 1,
    padding: 16,
    minHeight: 116,
  },
  communityImage: {
    width: 42,
    height: 42,
    borderRadius: 21,
    marginBottom: 10,
  },
  communityName: {
    ...typographyTokens.bodyAccent,
  } as any,
  communityText: {
    marginTop: 6,
    ...typographyTokens.body,
  } as any,
  placeholder: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  placeholderText: {
    color: '#FFFFFF',
    ...typographyTokens.uiH2,
  } as any,
});
